/* eslint-disable react-refresh/only-export-components -- route module: exports the router factory + route components together (not a fast-refresh boundary). */
import { useEffect, useRef, type ReactNode } from "react";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { ApiClient } from "@cntrl/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { IS_NON_PRODUCTION } from "../config/env.js";
import { RequireBand } from "../app/RequireBand.js";
import { useMobileBack } from "../app/mobileBack.js";
import { fetchMe } from "../app/auth/session.js";
import { requireInbox } from "./inboxGuard.js";
import { sendNonAdminsHome } from "./adminGuard.js";
import { fetchPlan, isPlanActive } from "../app/hooks/useBilling.js";
import { useMe } from "../app/hooks/useSession.js";
import { ActivateScreen } from "../features/billing/ActivateScreen.js";
import { RosterShell } from "../shells/roster/RosterShell.js";
import { openTeamPanel } from "../app/teamPanel.js";
import { useOrgCapability } from "../app/orgContext.js";
import { rosterTabRoute, rosterTabKeyForSegment } from "../shells/roster/rosterTabs.js";
import { RosterBandProvider } from "../shells/roster/RosterBandProvider.js";
import { RosterArtistDashboard } from "../features/org/RosterArtistDashboard.js";
import { RosterArtistHeartbeat } from "../features/org/RosterArtistHeartbeat.js";
import { BookingArtistDashboard } from "../features/org/BookingArtistDashboard.js";
import { ArtistRequiredGate } from "../features/org/ArtistRequiredGate.js";
import { TaskDrawer } from "../features/tasks/TaskDrawer.js";
import { useResolvedOrg, useRosterArtists } from "../app/hooks/useRoster.js";
import { switchRosterArtist } from "../app/switchRosterArtist.js";
import { useThemePreference, resolveEffectiveTheme } from "../app/theme.js";
import { useRealtimeOrNull } from "../app/realtime/RealtimeProvider.js";
import { PartnerShell } from "../shells/partner/PartnerShell.js";
import { AdminShell } from "../shells/admin/AdminShell.js";
import { AdminConsole } from "../features/admin/AdminConsole.js";
import { parseAdminTab, type AdminTab } from "../features/admin/adminTabs.js";
import { PartnerScreen } from "../features/partner/PartnerScreen.js";
import { PartnerDetailScreen } from "../features/partner/PartnerDetailScreen.js";
import { RosterHomeScreen } from "../features/org/RosterHomeScreen.js";
import { BookingMobileDashboard, LabelMobileDashboard, ManagementMobileDashboard } from "../features/org/RosterMobileArtistHome.js";
import { isSingleArtistMobileLens, rememberRosterArtist, resolveRosterArtistId } from "../app/rosterLastArtist.js";
import { BookingRosterScreen } from "../features/org/BookingRosterScreen.js";
import { RosterHeartbeatScreen } from "../features/org/RosterHeartbeatScreen.js";
import { OrgTeamScreen } from "../features/org/OrgTeamScreen.js";
import { OrgSettingsScreen } from "../features/org/OrgSettingsScreen.js";
import { LoginScreen } from "../features/auth/LoginScreen.js";
import { ForgotPasswordScreen } from "../features/auth/ForgotPasswordScreen.js";
import { ResetPasswordScreen } from "../features/auth/ResetPasswordScreen.js";
import { VerifyEmailScreen } from "../features/auth/VerifyEmailScreen.js";
import { ApplyPage } from "../features/apply/ApplyPage.js";
import { WelcomeScreen } from "../features/welcome/WelcomeScreen.js";
import { TourScreen } from "../features/welcome/TourScreen.js";
import { hidesPricing } from "../features/apply/applyPlans.js";
import { JoinScreen } from "../features/auth/JoinScreen.js";
import { PendingInvitesHost } from "../features/invites/PendingInvitesHost.js";
import { NotificationDetailsHost } from "../features/notifications/NotificationDetailsHost.js";
import { NotificationBandPromptHost } from "../features/notifications/NotificationBandPromptHost.js";
import { openPendingInvites } from "../app/pendingInvitesPanel.js";
import { useActingAs } from "../app/hooks/useDevFixtureSwitch.js";
import { usePushRegistration } from "../app/pushRegistration.js";
import { useLiveActivitySync } from "../app/liveActivitySync.js";
import { switchBand } from "../app/switchBand.js";
import { useActiveBand, useResolvedActiveBandId } from "../app/activeBand.js";
import { ArtistShell } from "../shells/artist/ArtistShell.js";
import { MobileShell } from "../shells/mobile/MobileShell.js";
import { hasSignedInOnThisDevice, isNative, markSignedInOnThisDevice } from "@cntrl/native";
import { EmptyState, Spinner } from "@cntrl/ui";
import { ShowVipScreen } from "../features/mobile/ShowVipScreen.js";
import { MobileShowsScreen } from "../features/mobile/MobileShowsScreen.js";
import { MobileReleasesScreen } from "../features/mobile/MobileReleasesScreen.js";
import { RoadCasesScreen } from "../features/mobile/RoadCasesScreen.js";
import { ScanScreen } from "../features/mobile/ScanScreen.js";
import { DayOfShowScreen } from "../features/mobile/DayOfShowTab.js";
import { ShowLogisticsScreen } from "../features/mobile/ShowLogisticsScreen.js";
import { RealtimeProvider } from "../app/realtime/RealtimeProvider.js";
import { ToastHost } from "../app/ToastHost.js";
import { ProfileHost } from "../features/contacts/ProfileHost.js";
import { openPersonProfile } from "../app/profilePanel.js";
import { useMessagePanel } from "../app/messagePanel.js";
import { useAuth } from "../app/auth/useAuth.js";
import { DashboardScreen } from "../features/dashboard/DashboardScreen.js";
import { MobileHome } from "../features/mobile/home/MobileHome.js";
import { useBandContextOrNull, useCapability } from "../app/bandContext.js";
import { useRefreshBookingLists } from "../app/hooks/useShows.js";
import { useBookingOffers, useConvertBookingOffer } from "../app/hooks/useBookingOffers.js";
import type { ShowCreateSeed } from "../features/booking/showCreateSeed.js";
import { AssetsScreen } from "../features/assets/AssetsScreen.js";
import { ProductionScreen } from "../features/assets/ProductionScreen.js";
import { BusesScreen } from "../features/buses/BusesScreen.js";
import { MobileBusesScreen } from "../features/buses/MobileBusesScreen.js";
import { BookingScreen } from "../features/booking/BookingScreen.js";
import { OpsCalendarScreen } from "../features/booking/OpsCalendarScreen.js";
import { BookingCalendarScreen } from "../features/org/BookingCalendarScreen.js";
import { ShowEditorModal } from "../features/booking/ShowEditorModal.js";
import { ShowPreview } from "../features/booking/ShowPreview.js";
import { GuestListModal } from "../features/guests/GuestListModal.js";
import { DoorCheckInScreen } from "../features/guests/DoorCheckInScreen.js";
import { AntennaHubScreen } from "../features/antenna/AntennaHubScreen.js";
import { AnnouncementComposerModal } from "../features/antenna/AnnouncementComposerModal.js";
import { FanClubSettingsModal } from "../features/antenna/FanClubSettingsModal.js";
import { BackstageManagerModal } from "../features/antenna/BackstageManagerModal.js";
import { CompsManagerModal } from "../features/antenna/CompsManagerModal.js";
import { AntennaHubGate } from "../features/antenna/AntennaHubGate.js";
import { ContactsScreen } from "../features/contacts/ContactsScreen.js";
import { MobileBandProfileScreen } from "../features/contacts/MobileBandProfileScreen.js";
import { MobileContactProfileScreen } from "../features/contacts/MobileContactProfileScreen.js";
import { MobileContactsScreen } from "../features/contacts/MobileContactsScreen.js";
import { MusicScreen } from "../features/music/MusicScreen.js";
import { DebugScreen } from "../features/diagnostics/DebugScreen.js";
import { parseDebugTab, type DebugTab } from "../features/diagnostics/debugTabs.js";
import { MarketingScreen } from "../features/marketing/MarketingScreen.js";
import { AdCreatorModal } from "../features/marketing/adcreator/AdCreatorModal.js";
import { FansScreen } from "../features/marketing/fans/FansScreen.js";
import { BroadcastComposerModal } from "../features/marketing/fans/BroadcastComposerModal.js";
import { BroadcastStatsModal } from "../features/marketing/fans/BroadcastStatsModal.js";
import { ContentScreen, ContentPlatformScreen } from "../features/content/ContentScreen.js";
import { InboxScreen } from "../features/inbox/InboxScreen.js";
import { useArtistShowLink, useRosterShowLink } from "../app/showLink.js";
import { NotificationsScreen } from "../features/notifications/NotificationsScreen.js";
import { ProfileModal } from "../features/profile/ProfileModal.js";
import { SettingsScreen, type SettingsTab } from "../features/settings/SettingsScreen.js";
import { SettingsAccountTab } from "../features/settings/SettingsAccountTab.js";
import { CommunicationPreferencesScreen } from "../features/settings/communicationPreferences/CommunicationPreferencesScreen.js";
import { SettingsProfileTab } from "../features/settings/SettingsProfileTab.js";
import { SettingsConnectionsTab } from "../features/settings/SettingsConnectionsTab.js";
import { SettingsTeamTab } from "../features/settings/SettingsTeamTab.js";
import { SettingsTutorialsTab } from "../features/settings/tutorials/SettingsTutorialsTab.js";
import { ReleasesScreen } from "../features/releases/ReleasesScreen.js";
import { NewReleaseModal } from "../features/releases/NewReleaseModal.js";
import { ReleaseDetailScreen } from "../features/releases/ReleaseDetailScreen.js";
import { OverviewSection } from "../features/releases/sections/OverviewSection.js";
import { AssetsSection } from "../features/releases/sections/AssetsSection.js";
import { ToDoSection } from "../features/releases/sections/ToDoSection.js";
import { SmartLinkSection } from "../features/releases/sections/SmartLinkSection.js";
import { LiveAdsSection } from "../features/releases/sections/LiveAdsSection.js";
import { DetailsSection } from "../features/releases/sections/DetailsSection.js";
import { TimelineSection } from "../features/releases/sections/TimelineSection.js";
import { BudgetSection } from "../features/releases/sections/BudgetSection.js";
import { DEFAULT_SECTION, RELEASE_SECTIONS, type ReleaseSectionKey } from "../features/releases/sections.js";
import { PhoneLayoutProvider, usePhoneLayout, useShellPhoneDecision } from "../app/phoneLayout.js";

/** Clamp `?step` to 1-10 (defaults 1) so the wizard is refresh/deep-link safe.*/
function clampStep(raw: unknown): number {
  const n = typeof raw === "string" ? Number.parseInt(raw, 10) : typeof raw === "number" ? raw : Number.NaN;
  return Number.isInteger(n) && n >= 1 && n <= 10 ? n : 1;
}

/** The router context — carried into every `beforeLoad` guard (auth is a LIVE read of the TokenProvider). */
export interface RouterContext {
  auth: { isAuthenticated: () => boolean };
  api: ApiClient;
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  // The Pending invites modal rides the ROOT (ART-239): it is opened from every shell's account dropdown and from a
  // seat_invite bell row or push, for a person who may be in any shell — so it is mounted once, here.
  component: () => (
    <>
      <Outlet />
      <PendingInvitesHost />
      <NotificationDetailsHost />
      {/* Opening a notification that belongs to another of your bands asks before switching — from any bell or a push. */}
      <NotificationBandPromptHost />
    </>
  ),
});

// ---- Public routes ------------------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string; via?: "welcome" } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    via: search.via === "welcome" ? "welcome" : undefined,
  }),
  // Pre-paint guard: an already-authenticated visitor never sees the login form. HONOR the ?redirect
  // so a login-bounce lands on its target even when the session is already live — e.g. an authed web user
  // full-loading the SSR /scan?token= page and tapping "Log in to update" reaches the in-app scan/toggle route
  // instead of dead-ending on the dashboard. Same-origin by construction (the auth guards + the /scan page only
  // ever set a same-origin path); the login component's onAuthenticated uses the same value.
  beforeLoad: async ({ context, search }) => {
    if (context.auth.isAuthenticated()) throw redirect({ to: search.redirect ?? "/" });
    // ART-346: in the app, a phone nobody has signed in on opens on Welcome rather than the login form. Welcome's
    // own "I already have an account" comes back here with `via=welcome`, which is what stops the loop.
    if (isNative() && search.via !== "welcome" && !(await hasSignedInOnThisDevice())) {
      throw redirect({ to: "/login/welcome" });
    }
  },
  component: LoginRoute,
});

// ART-346: the iOS app's signed-out first screens — Welcome and the four-step tour. Nested under the already-reserved
// `login` segment so they reserve no band slug. Native only: the web keeps going straight to /login.
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/welcome",
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated()) throw redirect({ to: "/" });
    if (!isNative()) throw redirect({ to: "/login" });
  },
  component: WelcomeRoute,
});

const tourRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/tour",
  validateSearch: (search: Record<string, unknown>): { step?: number } => {
    const n = Number(search.step);
    return { step: Number.isInteger(n) && n >= 1 && n <= 4 ? n : undefined };
  },
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated()) throw redirect({ to: "/" });
    if (!isNative()) throw redirect({ to: "/login" });
  },
  component: TourRoute,
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotRoute,
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset",
  validateSearch: (search: Record<string, unknown>): { token?: string; setup?: boolean } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    // `&setup=1` — the approved-application email's set-password link (the same token as a reset).
    setup: search.setup === "1" || search.setup === 1 || search.setup === true ? true : undefined,
  }),
  component: ResetRoute,
});

// The destination of a notification email's footer links (ART-285): `/settings/email?token=…` for "Update your email
// preferences", `&unsubscribe={category}` for "unsubscribe" (the email's own switch). Top-level and OUTSIDE the
// shell, like /reset — the token, not a session, is what authorizes it, so it works signed out. `settings` is
// already a reserved first segment, so this path costs no new band-slug reservation; it does not collide with the
// shell's /settings children (none is "email").
const communicationPreferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/email",
  validateSearch: (search: Record<string, unknown>): { token?: string; unsubscribe?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    unsubscribe: typeof search.unsubscribe === "string" ? search.unsubscribe : undefined,
  }),
  component: CommunicationPreferencesRoute,
});

// The destination of `{AppBaseUrl}/verify-email?token=…`, which `IAuthService` has emailed since the flow
// shipped while this route did not exist (ART-7). Top-level and OUTSIDE the shell: the link is opened from a
// mail client, so signed-out is the ordinary case. Adding a top-level segment reserves it as a band slug —
// `alb.tf` + the gate function, kept in step by `ReservedBandSlugParityTests`.
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: VerifyEmailRoute,
});

// The destination of every seat invite's link (ART-239) — crew, org team, partner: `{AppBaseUrl}/join?token=…`.
// Top-level and OUTSIDE the shell, because the person following it usually has no account yet. A top-level
// segment reserves a band slug — `alb.tf` (dev + prod), the box Caddyfile and the gate function (909), kept in step
// by `ReservedBandSlugParityTests`.
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: JoinRoute,
});

// The public application page (.design-specs/apply.plan.md): new bands, labels, managers and agencies apply here
// and a platform admin approves them. Open signed in or out — an existing member applying for another band (the
// account menus' "+ New band") uses it too. `?mode=roster` opens the company form; `?plan=pro` preselects Pro. A top-level
// segment reserves a band slug — `alb.tf` (dev + prod), the box Caddyfile and the gate function (929), kept in step
// by `ReservedBandSlugParityTests`.
const applyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apply",
  validateSearch: (
    search: Record<string, unknown>,
  ): { mode?: "roster"; plan?: "pro"; from?: "ios"; storefront?: string } => ({
    mode: search.mode === "roster" ? "roster" : undefined,
    plan: search.plan === "pro" ? "pro" : undefined,
    // ART-346: the iOS app's Apply online opens this page in Safari with its App Store storefront (ISO alpha-3).
    from: search.from === "ios" ? "ios" : undefined,
    storefront: typeof search.storefront === "string" ? search.storefront : undefined,
  }),
  component: ApplyRoute,
});

// ---- Authenticated shell (pathless layout route with the pre-paint auth guard) ------------------------

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  // Pre-paint guards, in order: no session → /login (capturing where the user was headed), then the
  // subscription paywall.
  //
  // **The paywall lives HERE, on the pathless layout every authed route hangs off, and not on the dashboard**
  // (moved 2026-09-10). It was on `/` alone, so it only fired for someone who LANDED on the dashboard: a deep
  // link to `/settings/account`, `/booking`, `/content` — or a browser restoring a tab — rendered the app
  // fully for a band with no subscription. The server's 402 still fenced every WRITE and reads are
  // deliberately open ("a lapsed band must still read what it put in"), so nothing leaked; but the guard's own
  // comment promised "an inactive band never flashes the app", and for every entry point except one that was
  // untrue. Found by an e2e spec deep-linking to Settings and never being redirected.
  //
  // `/activate` hangs off the ROOT route, not off this one, so it cannot loop through here — that is what
  // makes this the safe place to put it rather than a redirect that has to exempt itself.
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    await sendAdminsToConsole(context);
    await confinePartner(context, location.pathname);
    await requirePaidBand(context);
  },
  component: ShellLayout,
});

/**
 * A seatless partner (ART-239) has their own surface; of the band app they open ONE page, a show's Day of Show at
 * `/dos?show=`. Anything else under this shell sends them home rather than drawing band chrome they have no seat for.
 * Fails OPEN on a `/me` error like the guards beside it — the server fences their reads either way.
 */
async function confinePartner(context: RouterContext, pathname: string): Promise<void> {
  let me;
  try {
    me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
  } catch {
    return;
  }
  if (me.isPartnerViewer && pathname !== "/dos") throw redirect({ to: "/partner" });
}

/**
 * An admin account is DEDICATED to the admin console (product owner, 2026-10-05): it holds no seat and has no app
 * to open. So any app route it lands on (a bookmark, a typed URL, the post-login "/") sends it to `/admin`. Fails
 * OPEN on a `/me` error, like every guard here — the server fences the data either way (an admin holds no seat,
 * so it reaches no band or org).
 */
async function sendAdminsToConsole(context: RouterContext): Promise<void> {
  let me;
  try {
    me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
  } catch {
    return;
  }
  if (me.isPlatformAdmin) throw redirect({ to: "/admin" });
}

/**
 * The subscription paywall (P-08) — the ONE implementation, inherited by every authed route.
 *
 * Fails OPEN at both fetches, deliberately: a transient 5xx on `/me` or on the plan read must never trap a
 * paying band outside their own app. The redirect is thrown OUTSIDE the try blocks so it is never swallowed
 * by the very catch that exists to be lenient.
 *
 * Returns early for anyone with no band to check — a seatless Partner, an org-only staffer, a band seat whose
 * id has not resolved. Those are not "unpaid", they are "not a band", and the roster has its own gates.
 */
async function requirePaidBand(context: RouterContext): Promise<void> {
  let me;
  try {
    me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
  } catch {
    return;
  }
  if (me.isPartnerViewer) return;
  const bandSeat = me.seats?.find((s) => s.kind === "band");
  const bandId = bandSeat ? (me.activeBandId ?? bandSeat.bandId) : undefined;
  if (!bandId) return;

  let plan;
  try {
    plan = await context.queryClient.ensureQueryData({
      queryKey: ["band", bandId, "plan"],
      queryFn: () => fetchPlan(context.api, bandId),
    });
  } catch {
    return;
  }
  // `isPlanActive`, not an inline comparison — it is the client mirror of the server's BillingStatus.IsActive,
  // and the two must agree about `past_due` (which grants access) or a band mid-dunning is bounced out of an
  // app that would have accepted every write it made.
  if (!isPlanActive(plan.status)) throw redirect({ to: "/activate" });
}

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  // The task drawer/create modal is a routed-modal `?task=:id` (URL-addressable) — refresh/back/
  // deep-link all work because the open task rides the URL. `new` opens the create modal.
  validateSearch: (search: Record<string, unknown>): { task?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
  }),
  // Account-root shell selection — a PRE-PAINT guard so an org-root never flashes the artist
  // shell. Ensure /me (the shell reuses this cached read), then: a band seat → the artist dashboard here; no
  // band but an org seat → the org shell (/roster); no seat at all → /apply (2026-10-15: there is no self-serve
  // create any more — a band or a company joins by applying). A /me fetch
  // failure falls through to the dashboard, whose own me-error state renders (never a crash).
  beforeLoad: async ({ context, location }) => {
    let me;
    try {
      me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
    } catch {
      return;
    }
    // `?invites=open` (a seat_invite bell row or push, ART-239): every redirect below drops search params, so open
    // the root-mounted Pending invites modal HERE, before any of them, rather than leaving it to the param.
    if ((location.search as Record<string, unknown>).invites === "open") openPendingInvites();
    // A seatless Personal Viewer (Partner) routes STRAIGHT to their read-only surface — they are
    // server-confined to it regardless; this is the client cue. Checked before the seat branches (they have none).
    if (me.isPartnerViewer) throw redirect({ to: "/partner" });
    // SHELL SELECTION only. The subscription paywall used to live here too and has moved UP to `shellRoute`,
    // which every authed route inherits — this route answers "where should you LAND", which is a question
    // only `/` has, while the paywall is a question every authed route has. Keeping a copy here would be two
    // authorities over one redirect.
    const bandSeat = me.seats?.find((s) => s.kind === "band");
    if (bandSeat) return;
    if (me.seats?.some((s) => s.kind === "org")) throw redirect({ to: "/roster" });
    throw redirect({ to: "/apply" });
  },
  component: DashboardRoute,
});

// Booking (dev): the shows list + create form, with the guest-list modal as a routed child that overlays it
// (the routed-modal rule, — refresh/back/deep-link all work because the modal is URL-addressable).
// `?tab=confirmed|holds|past` selects the shows card's tab. It is a SEARCH PARAM rather than local state
// alone so something outside the list can steer it — "Confirm hold and save" lands on Confirmed, where the
// show it just promoted now lives (Ryan, 2026-08-23). Leaving the user on Holds after a successful confirm
// showed them the tab the show had just left, which reads as the promotion not having worked.
// Absent/garbage falls back to `confirmed`, the default the card opens on.
const BOOKING_TABS = ["confirmed", "holds", "past"] as const;
type BookingTabParam = (typeof BOOKING_TABS)[number];

/** Absent or garbage falls back to `confirmed`, the tab the card opens on. */
function bookingTab(value: unknown): BookingTabParam {
  return BOOKING_TABS.includes(value as BookingTabParam) ? (value as BookingTabParam) : "confirmed";
}

const bookingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/booking",
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: BookingRoute,
});

const guestsModalRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "shows/$showId/guests",
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: GuestsModalRoute,
});

// The money-free ops-calendar variant for the read-only-Schedule tier — a sibling of /booking.
const scheduleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/calendar",
  // `?date=YYYY-MM-DD` opens the calendar on that day — the chat's Day of Show link for a seat whose schedule
  // surface is this calendar rather than Booking (showLink.ts). Anything else is ignored.
  validateSearch: (s: Record<string, unknown>): { date?: string; show?: string } => ({
    date: typeof s.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.date) ? s.date : undefined,
    // `?show={id}` opens that show's event sheet — where a "you've been paid" notification lands (ART-305 v3).
    show: typeof s.show === "string" && /^[0-9a-f-]{36}$/i.test(s.show) ? s.show : undefined,
  }),
  component: ScheduleRoute,
});

// The calendar lived at /schedule until ART-305 renamed the URL to match its label. Old links, bookmarks and any
// notification route written before the rename still land on it, `?date=` carried across.
const legacyScheduleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/schedule",
  validateSearch: (s: Record<string, unknown>): { date?: string } => ({
    date: typeof s.date === "string" ? s.date : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/calendar", search: search.date ? { date: search.date } : {} });
  },
});

// The show editor — a routed MODAL over /booking, like the guest list above it: the list stays mounted
// behind the scrim, and refresh / back / deep-link still work because the overlay is URL-addressable. Add and
// edit are ONE component (`ShowEditorModal`), which is the point — the module spec's rule is "+ Add show and
// Edit open the same popup … don't build any of them as a screen".
//
// The URLs are byte-identical to the full-screen routes these replaced (`e2e/helpers.ts` deep-links them).
// `new` is static so it wins over the `$showId` param sibling — the router ranks by FULL PATH in one segment
// trie, so the preview route being a shell sibling rather than a booking child does not change that.
// `?step=1-10` drives the active section.
// Each editor URL carries `tab` alongside `step`: a child validates its OWN search, so a key it does not
// name is dropped from the URL entirely. Without it, opening a modal from Holds reset the list behind the
// scrim to the `confirmed` default and closing landed on the wrong tab.
const showNewRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "shows/new",
  validateSearch: (search: Record<string, unknown>): { step: number; tab: BookingTabParam } => ({
    step: clampStep(search.step),
    tab: bookingTab(search.tab),
  }),
  // The SAME component as `showEditRoute` — see `ShowEditorRoute`: two functions would remount the dialog
  // when create swaps the URL.
  component: ShowEditorRoute,
});

const showEditRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "shows/$showId/edit",
  validateSearch: (search: Record<string, unknown>): { step: number; tab: BookingTabParam } => ({
    step: clampStep(search.step),
    tab: bookingTab(search.tab),
  }),
  component: ShowEditorRoute,
});

// The preview is a routed MODAL over /booking (PO ruling 2026-08-23, TASK-057), reversing audit ruling 10:
// the module spec is explicit that View "open[s] a fixed-position popup on top of the Booking page — no URL
// change, no navigation", and the full-screen route unmounted the list behind it.
//
// The path is byte-identical to the shell-sibling route this replaces, which is the whole point: it is still
// the target every notification deep link resolves to (NotificationDispatcher, DosReminderSweepService,
// DosReminderSweepService, FlightStatusPollerService all emit `/booking/shows/{showId}`). As a booking CHILD
// a cold start now renders Booking underneath the modal rather than a bare workbench, so those links keep
// working AND gain a page to close back onto.
//
// `shows/new` and `shows/$showId/edit` stay static-first siblings; the router ranks by full path in one
// segment trie, so `shows/$showId` cannot shadow them.
const showPreviewRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "shows/$showId",
  // `tab` is optional in practice — a notification deep-links here with no search at all, and falls back to
  // `confirmed`, which is the right landing for a cold start with no list behind it yet.
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: ShowPreviewRoute,
});

// Mobile door check-in — a full-screen sibling route, not nested under booking.
const doorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shows/$showId/guests",
  component: DoorRoute,
});

// Mobile Day-of-Show tab (`/dos`, the center DOS button destination) — a full-screen sibling.
// The tab resolves today's show (or list mode); its essentials route to the door / logistics.
const dosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/dos",
  component: DosRoute,
  // `?show=<id>` remembers which show the DOS tab has open, so returning from a per-show sub-screen
  // (VIP / door / logistics) via history-back reopens it rather than re-resolving to auto/list
  // (ART-153 pass two).
  // `?via=link` marks a show opened by a LINK from another screen (the chat's Day of Show link) rather than picked
  // inside the tab — then its back, and the iOS swipe, return to that screen (ART-268/271). A pick in the tab
  // rewrites the URL without it; a return from VIP / door / logistics restores the same entry, mark included.
  // `?band=<id>` comes from a tap on the lock-screen Live Activity (ART-208): the show belongs to that band, which
  // may not be the one open, so the route switches to it first and then drops the param.
  validateSearch: (s: Record<string, unknown>): { show?: string; via?: "link"; band?: string } => ({
    show: typeof s.show === "string" ? s.show : undefined,
    via: s.via === "link" ? "link" : undefined,
    band: typeof s.band === "string" ? s.band : undefined,
  }),
});

// Mobile shows LIST (`/shows`, the index) — the read-only shows/holds list the booking `shows` tab and the
// TM's `guest_list` tab both open (ART-153 pass two), instead of the desktop `/booking` pipeline they used to
// dump every mobile seat on. Money-free; a confirmed row links to its per-show door + logistics. `shows` is
// already a reserved band slug (the child routes below), so the index adds no new reserved segment.
const showsListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shows",
  component: ShowsListRoute,
});

// Mobile show VIP + logistics — full-screen siblings of the door route; stubbed until /S4.
const showVipRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shows/$showId/vip",
  component: ShowVipRoute,
});

const showLogisticsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shows/$showId/logistics",
  component: ShowLogisticsRoute,
});

// NFC road cases — create/pair the band's road cases; reached from the Case-Checklist home widget.
const roadCasesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/road-cases",
  component: RoadCasesScreen,
});

// The in-app NFC-sticker landing. A tapped sticker's URL `/scan?token=…` is routed into the app by
// AASA (+ the deep-link router — `scan` is on the same-origin allowlist); it resolves the case + toggles loaded.
const scanRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/scan",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ScanRoute,
});

// Antenna fan-club hub (`/antenna`, shot-37 list-only) + the composer as routed-modal children that
// overlay it (the routed-modal rule — refresh/back/deep-link all work). `new` is static so it wins over the
// `$id` param route (TanStack precedence).
const antennaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/antenna",
  component: AntennaRoute,
});

const announcementNewRoute = createRoute({
  getParentRoute: () => antennaRoute,
  path: "announcements/new",
  component: AnnouncementNewRoute,
});

const announcementEditRoute = createRoute({
  getParentRoute: () => antennaRoute,
  path: "announcements/$id/edit",
  component: AnnouncementEditRoute,
});

// The fan-club management modals (shot-42 settings; backstage + comps) — routed-modal children that
// overlay the hub through its existing <Outlet/>. Static paths so they win over any param route (precedence).
const antennaSettingsRoute = createRoute({
  getParentRoute: () => antennaRoute,
  path: "settings",
  component: AntennaSettingsRoute,
});

const antennaBackstageRoute = createRoute({
  getParentRoute: () => antennaRoute,
  path: "backstage",
  component: AntennaBackstageRoute,
});

const antennaCompsRoute = createRoute({
  getParentRoute: () => antennaRoute,
  path: "comps",
  component: AntennaCompsRoute,
});

// Releases hub (`/releases`, shot-08) + the new-release type picker as a routed-modal child that
// overlays it (shot-47) via the hub's <Outlet/> — refresh/back/deep-link all work. `new` is static so it wins
// over the `$releaseId` detail route (TanStack precedence — the showNew/showPreview precedent).
const releasesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/releases",
  // `?release=` — on a phone, the release the catalogue opens on. A single release's page redirects here below
  // `lg`, so every door onto a release (Needs attention, the release card, a notification) lands on the phone screen.
  validateSearch: (search: Record<string, unknown>): { release?: string } => ({
    release: typeof search.release === "string" && search.release !== "" ? search.release : undefined,
  }),
  component: ReleasesRoute,
});

const releasesNewRoute = createRoute({
  getParentRoute: () => releasesRoute,
  path: "new",
  component: ReleasesNewRoute,
});

// The release command center (`/releases/:id/{overview,…}`, shots 09–16) — the shell renders the
// hero/share-bar/section-strip/pager + the routed section <Outlet/>; the index redirects to Overview. Each
// numbered section is a child route so it is deep-linkable + refresh-safe (mirrors the settings parent+children).
const releaseDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/releases/$releaseId",
  component: ReleaseDetailRoute,
});

const releaseIndexRoute = createRoute({
  getParentRoute: () => releaseDetailRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: `/releases/${(params as { releaseId: string }).releaseId}/${DEFAULT_SECTION}` });
  },
});

const releaseOverviewRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "overview", component: OverviewSectionRoute });
const releaseAssetsRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "assets", component: AssetsSectionRoute });
const releaseTodoRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "todo", component: ToDoSectionRoute });
const releaseSmartLinkRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "smart-link", component: SmartLinkSectionRoute });
const releaseAdsRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "ads", component: LiveAdsSectionRoute });
const releaseDetailsRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "details", component: DetailsSectionRoute });
const releaseTimelineRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "timeline", component: TimelineSectionRoute });
const releaseBudgetRoute = createRoute({ getParentRoute: () => releaseDetailRoute, path: "budget", component: BudgetSectionRoute });

// Contacts rolodex — band-scoped `/contacts`. The add/edit editor is an in-screen modal
// (not a routed child), so this is a single leaf route like `/calendar`.
const contactsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contacts",
  component: ContactsRoute,
});

// The MOBILE friend/band profile (ART-195), a full-screen screen versus the desktop modal. A FLAT
// `shellRoute` child with a nested path string — first segment is still `contacts` (already a reserved band
// slug), so it reserves nothing new (the `/socials/$platform` precedent). Desktop deep-links bounce back to
// `/contacts` in the wrapper.
const contactsBandRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contacts/band/$bandId",
  component: ContactsBandRoute,
});

// The MOBILE contact/person profile (ART-194), the full-screen counterpart of the desktop CrewProfileModal.
// Same nesting rationale as the band route above (first segment `contacts`, already reserved).
const contactsPersonRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contacts/person/$personId",
  component: ContactsPersonRoute,
});

const musicRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/music",
  component: MusicRoute,
});

// `/debug` — the developer diagnostic surface (tabbed; `?tab=`). REGISTERED ONLY IN A NON-PRODUCTION LANE (see the route tree
// below): in production `/debug` resolves to Not Found, exactly as if it had never been written.
//
// It is guarded on BOTH sides, deliberately and with the same allowlist. The API refuses unless EnvName is a
// recognised non-production lane (SongstatsDebugController.RefuseInProduction), and the SPA does not mount the
// route at all. Either alone would do; both means a mistake in one does not expose raw vendor payloads.
const debugRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/debug",
  validateSearch: (search: Record<string, unknown>): { tab?: DebugTab } =>
    search.tab === undefined ? {} : { tab: parseDebugTab(search.tab) },
  component: DebugRoute,
});

const marketingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/marketing",
  component: MarketingRoute,
});

// The Ad Creator routed modal (shots 45–46) — overlays the marketing campaigns table via its own backdrop.
// `?release=` (+ `?dest=` smart link) pre-fills a release-promote when opened from the release Live-Ads section.
const marketingCampaignNewRoute = createRoute({
  getParentRoute: () => marketingRoute,
  path: "campaigns/new",
  validateSearch: (search: Record<string, unknown>): { release?: string; dest?: string } => ({
    release: typeof search.release === "string" ? search.release : undefined,
    dest: typeof search.dest === "string" ? search.dest : undefined,
  }),
  component: MarketingCampaignNewRoute,
});

// Fan list + broadcast composer (marketing/fans) — a FULL screen sibling of /marketing (the
// /socials vs /socials/$platform precedent), fans:read gated inside. Its routed children are the log-only broadcast
// composer + the deep-linkable per-broadcast stats modal (add-fan, fan detail, email style are transient overlays).
const fansRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/marketing/fans",
  component: FansRoute,
});
const broadcastNewRoute = createRoute({
  getParentRoute: () => fansRoute,
  path: "broadcasts/new",         // static wins over the $broadcastId param route (precedence)
  component: BroadcastNewRoute,
});
const broadcastStatsRoute = createRoute({
  getParentRoute: () => fansRoute,
  path: "broadcasts/$broadcastId",
  component: BroadcastStatsRoute,
});

// Socials tab — the social command center + AI tools, plus the /socials/$platform drill-down.
// The nav KEY stays `content` (NavigationDocument already labels it "Socials"); only the URL moved.
const contentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/socials",
  component: ContentRoute,
});
const contentPlatformRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/socials/$platform", // TanStack uses $param, not :param
  component: ContentPlatformRoute,
});

/** `?with={personId}` on the two inbox list routes (ART-264). */
function messageWithSearch(search: Record<string, unknown>): { with?: string } {
  return { with: typeof search.with === "string" && search.with.length > 0 ? search.with : undefined };
}

// Messages inbox (shot-48) — the routed `/messages` (list, no selection) + `/messages/$threadId`
// (the open thread rides the URL: refresh/back/deep-link all work). PERSON-scoped — no <RequireBand> for the
// list; sending an OFFER needs the active band, enforced in the composer. Desktop = two-pane modal, mobile =
// full-screen list + thread pages. Both routes mount the same modal; the param route just carries the selection.
const messagesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/messages",
  // `?with={personId}` — "message this person" from a surface that is not the inbox (ART-264). On a phone the
  // inbox is this page, so the request rides the URL into InboxScreen's own open-with rule.
  validateSearch: messageWithSearch,
  beforeLoad: requireInbox,
  component: MessagesRoute,
});

const messageThreadRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/messages/$threadId",
  // BOTH routes, not just the list. `/messages/{id}` is the shape a notification deep-link and a restored
  // tab actually use, so guarding only the parent path leaves the one entry point that matters open.
  beforeLoad: requireInbox,
  component: MessageThreadRoute,
});


// Notification center — the URL-addressable `/notifications` full page (mobile; the bell opens
// the drawer in place on desktop). PERSON-scoped — no <RequireBand> (a notification is the person's, cross-band).
const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/notifications",
  component: NotificationsRoute,
});

// Personal profile modal — the routed `/profile`. PERSON-scoped (not band-scoped): `?person`
// views another person's public profile; `?mode=onboarding` reframes edit-own for invite acceptance.
const profileRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/profile",
  validateSearch: (search: Record<string, unknown>): { person?: string; mode?: "onboarding" } => ({
    person: typeof search.person === "string" ? search.person : undefined,
    mode: search.mode === "onboarding" ? "onboarding" : undefined,
  }),
  component: ProfileRoute,
});

// Settings — the `/settings` parent renders the tab chrome + `<Outlet/>`; `/account` +
// `/features` are children; the index redirects `/settings` → `/settings/account` (mirrors the antenna
// parent+children block).
const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  component: SettingsRoute,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    // The page mirrors the modal: Artist Profile leads (SETTINGS_TABS order). A seat without
    // `connections:manage` is bounced on by the tab route itself, which is where the capability lives.
    throw redirect({ to: "/settings/profile" });
  },
});

const settingsAccountRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "account",
  component: SettingsAccountRoute,
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "profile",
  component: SettingsProfileRoute,
});

const settingsFeaturesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "features",
  component: SettingsFeaturesRoute,
});

const settingsConnectionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "connections",
  component: SettingsConnectionsRoute,
});

const settingsTeamRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "team",
  component: SettingsTeamRoute,
});

const settingsTutorialsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "tutorials",
  component: SettingsTutorialsTab,
});

// Assets — the parent tab hosts the sub-surfaces (Brand/Core now; Links/EPK/Production later). The index
// The nav surface points at /assets (the single stacked page).
const assetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/assets",
  component: AssetsRoute,
});

// /assets is ONE stacked page (CD Assets layout: the Core section + the collapsible EPK section —
// no sub-nav). The old sub-surface URLs redirect so bookmarks and older nav entries keep working.
const assetsIndexRoute = createRoute({
  getParentRoute: () => assetsRoute,
  path: "/",
  component: AssetsIndexRoute,
});

const assetsBrandRoute = createRoute({
  getParentRoute: () => assetsRoute,
  path: "brand",
  beforeLoad: () => {
    throw redirect({ to: "/assets" });
  },
});

const assetsLinksRoute = createRoute({
  getParentRoute: () => assetsRoute,
  path: "links",
  beforeLoad: () => {
    throw redirect({ to: "/assets" });
  },
});

const assetsEpkRoute = createRoute({
  getParentRoute: () => assetsRoute,
  path: "epk",
  beforeLoad: () => {
    throw redirect({ to: "/assets" });
  },
});

// Advance Docs (the production/venue stack) is its OWN page, deliberately NOT an /assets sub-surface
// (CD Assets scope rule: "do not render the production stack on /assets" — /assets is Core+EPK only;
// the desktop door is the Touring▾ leaf). The old /assets/production URL redirects.
const advanceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/advance",
  component: AdvanceRoute,
});

// PRO Buses & Trucks (ART-147). Hangs off shellRoute like /advance; the screen owns the Pro upsell and the
// AV-or-TM narrowing, so no beforeLoad tier guard here (a Standard deep-link sees the designed upsell, not a
// redirect). No roster twin — management is not an AV/TM seat and gets no bus surface (the visibility ruling).
const busesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/buses",
  component: BusesRoute,
});

const assetsProductionRoute = createRoute({
  getParentRoute: () => assetsRoute,
  path: "production",
  beforeLoad: () => {
    throw redirect({ to: "/advance" });
  },
});

// The old self-serve create-org page (2026-10-15): an organization now joins through /apply and a platform admin's
// approval, like a band. The path survives as a redirect to the company form so a bookmark or an old in-app link
// still lands somewhere true — and so `onboarding` stays a reserved band slug without a new gate revision.
const createOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/create-org",
  beforeLoad: () => {
    throw redirect({ to: "/apply", search: { mode: "roster" } });
  },
});

// The subscription paywall (P-08) — authed, full-screen (a PRE-SHELL gate, no artist
// chrome), like create-org. The dashboard guard redirects an inactive band here pre-paint; the Stripe checkout
// deep-links back to `/activate?checkout=done|canceled` (GATE-6 — designed here, fully exercised at Phase 4).
const activateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activate",
  validateSearch: (search: Record<string, unknown>): { checkout?: "done" | "canceled" } => ({
    checkout: search.checkout === "done" ? "done" : search.checkout === "canceled" ? "canceled" : undefined,
  }),
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: ActivateRoute,
});

// The RosterShell — the org-root chrome (240px artist rail + artist-scoped tab strip +
// subview chip + context header). A pathless authed layout; org roots land here (the account-root guard
// redirects them from "/"). Replaces the minimal OrgShell. The SAME feature modules mount under the org
// lens via RosterBandProvider (the iframes die here); an artist switch SCORCHES (the close).
const rosterShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_rosterShell",
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    // Second guard: this shell needs an ORG SEAT, and a person can be standing on one of its URLs without
    // one. The DEV fixture switcher hops a band member into an org lens, and a browser RELOAD returns them to
    // THEMSELVES (the swapped bearer is memory-only) on whatever /roster URL they were reading. With no guard
    // the shell resolved no org and rendered its "No organization yet" onboarding CTA — an artist who never
    // asked for an org, told to create one, on a page their seat cannot open. Bounce to "/" rather than
    // deciding here: the account-root guard is the ONE authority on where a seat lives (artist dashboard /
    // roster / partner / create-org), so this stays a plain "not here" and never a second copy of that
    // decision. A /me failure falls THROUGH, mirroring the account-root guard, so the shell's own me-error
    // state renders rather than a transient 5xx throwing someone out of the surface they were using.
    let me;
    try {
      me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
    } catch {
      return;
    }
    if (!me.seats?.some((s) => s.kind === "org")) throw redirect({ to: "/" });
  },
  component: RosterShellLayout,
});

// Org-level routes (All-roster home + the artist-required gate + team + org settings).
const rosterHomeRoute = createRoute({ getParentRoute: () => rosterShellRoute, path: "/roster", component: RosterHomeRoute });
const rosterGateRoute = createRoute({
  getParentRoute: () => rosterShellRoute,
  path: "/roster/g/$tab",
  // ART-331 — the Booking lens's calendar has an All-roster scope; `?type=` filters it like the artist one.
  validateSearch: (search: Record<string, unknown>): { type?: string } => ({
    type: typeof search.type === "string" ? search.type : undefined,
  }),
  component: RosterGateRoute,
});
const rosterTeamRoute = createRoute({ getParentRoute: () => rosterShellRoute, path: "/roster/team", component: OrgTeamScreen });

// MANAGEMENT'S INBOX (PO, 2026-09-11). The same two-pane modal the artist shell opens, mounted under the
// ROSTER shell so a manager reaches it without leaving their workspace — and gated by the same
// `requireInbox`, which is the whole point of that guard being a server answer rather than a seat check: it
// says no to label and booking here for exactly the reason it says no to the artist account there.
const rosterMessagesRoute = createRoute({
  getParentRoute: () => rosterShellRoute,
  path: "/roster/messages",
  // `?with={personId}` — "message this person" from a surface that is not the inbox (ART-264). On a phone the
  // inbox is this page, so the request rides the URL into InboxScreen's own open-with rule.
  validateSearch: messageWithSearch,
  beforeLoad: requireInbox,
  component: RosterMessagesRoute,
});

const rosterMessageThreadRoute = createRoute({
  getParentRoute: () => rosterShellRoute,
  path: "/roster/messages/$threadId",
  beforeLoad: requireInbox,
  component: RosterMessageThreadRoute,
});
const rosterSettingsRoute = createRoute({ getParentRoute: () => rosterShellRoute, path: "/roster/settings", component: OrgSettingsScreen });

// The per-artist layout (/roster/artists/$artistId) — mounts RosterBandProvider (the org-lens BandContext) around
// the routed module Outlet, so every shared module below renders under `bandId=artistId` + `shell='roster'`.
const rosterArtistRoute = createRoute({
  getParentRoute: () => rosterShellRoute,
  path: "/roster/artists/$artistId",
  component: RosterArtistLayout,
});

// The shared feature modules mount here under the org-lens BandContext (the iframes die). Most are
// navigation-agnostic (nav-free or callback-driven) and render fully under the roster lens. KNOWN SEAM
// (+ follow-up, documented in 2026-08-): a few modules carry HARD-CODED artist-shell
// navigation (MarketingScreen's "Fans/Connections/New campaign" cross-links; the release Overview "Checklist"
// + LiveAds "Boost" jumps) — those edge affordances navigate OUT of the roster lens into the artist shell (a
// lens-crossing seam, not a crash). The subview sessions that own the Marketing/Release surfaces under roster
// (Label / Mgmt) make those cross-links lens-aware. The PRIMARY view of every module renders
// correctly under the org lens with the server-computed (money-free for Label) capabilities — the target.
// `?task=:id` mirrors the artist shell's own routed task drawer. It lives on the per-artist LANDING because
// that is where every lens's To-do card is, and because a cross-roster to-do row has to land somewhere it can
// open: before this, clicking one navigated to the artist and stopped there ("you cant open task here",
// ART-119) — the deep-link the widget's own comment had been deferring.
const rosterArtistIndexRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>): { task?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
  }),
  component: RosterArtistDashboardRoute,
});
const rosterMusicRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "music", component: MusicScreen });
const rosterMarketingRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "marketing", component: MarketingScreen });
const rosterContentRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "content", component: ContentScreen });
// Touring▾ under the roster lens — the SAME two pages the artist shell serves, at the SAME two URLs, so an
// org seat and a band seat looking at one artist see one product. The tab KEY is `schedule` for every
// subview (server data); `rosterTabRoute` maps it to the segment, exactly as `buildNavItems` maps the one
// `schedule` surface to /booking or /calendar on the artist side.
//
// Until 2026-09-01 there was ONE roster route here, at `schedule`, always mounting `OpsCalendarScreen` — the
// money-free calendar — including for the Booking and Management lenses, whose tab is LABELLED "Booking"
// (rosterTabLabel has always forked that). So the label promised the cockpit, the route delivered the
// read-only calendar, and an agent had no way to open a show, edit a deal or reach a guest list from their
// own booking tab.
//
// Splitting it in two rather than forking inside one route is what keeps the URL honest in BOTH directions:
// a Booking lens is not parked on /calendar, and a Label lens — whose Calendar is read-only and money-free
// by P-06 — is not parked on /booking.
const rosterScheduleRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "calendar",
  // ART-331 — `?type=` filters the booking calendar to one deal state (the Active deals board links here).
  validateSearch: (search: Record<string, unknown>): { type?: string } => ({
    type: typeof search.type === "string" ? search.type : undefined,
  }),
  component: RosterCalendarRoute,
});

/** The Booking lens's calendar is the deal calendar (ART-331); every other lens keeps the ops calendar. */
function RosterCalendarRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterScheduleRoute.id });
  const { type } = useSearch({ from: rosterScheduleRoute.id });
  const { subview } = useResolvedOrg();
  if (subview !== "booking") return <OpsCalendarScreen />;
  return (
    <BookingCalendarScreen
      scope="artist"
      type={type}
      onTypeChange={(t) =>
        void navigate({ to: `/roster/artists/${artistId}/calendar`, search: t ? { type: t } : {}, replace: true, resetScroll: false })}
    />
  );
}

// The roster calendar's pre-ART-305 segment, kept as a redirect for old links.
const rosterLegacyScheduleRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "schedule",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/roster/artists/$artistId/calendar", params });
  },
});

// The cockpit, with the four routed overlays mirrored below — they are what make it the same PAGE rather
// than a screenshot of one: the show editor is how a show gets created at all.
const rosterBookingRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "booking",
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: RosterBookingRoute,
});

// The overlays, mirroring `/booking`'s children one-for-one. Each validates its OWN search — a key a child
// does not name is dropped from the URL, which is how opening a modal from Holds used to reset the list
// behind the scrim to `confirmed` (the artist-side bug, 2026-08-23; `router.test.ts` pins it for both trees).
const rosterShowNewRoute = createRoute({
  getParentRoute: () => rosterBookingRoute,
  path: "shows/new",
  // `offer` is the pre-hold shortlist row this create came from — set only by the board's "Convert to hold".
  // It rides the URL rather than component state so the seeded form survives a refresh and a deep link, which
  // is the whole reason the editor is a routed overlay; and it is only on the CREATE route, because an offer
  // has nothing to say to a show that already exists.
  validateSearch: (search: Record<string, unknown>): { step: number; tab: BookingTabParam; offer?: string } => ({
    step: clampStep(search.step),
    tab: bookingTab(search.tab),
    offer: typeof search.offer === "string" && search.offer !== "" ? search.offer : undefined,
  }),
  // The SAME component as the edit route, for the same reason it is on the artist side: two functions would
  // remount the dialog when create swaps the URL.
  component: RosterShowEditorRoute,
});

const rosterShowEditRoute = createRoute({
  getParentRoute: () => rosterBookingRoute,
  path: "shows/$showId/edit",
  validateSearch: (search: Record<string, unknown>): { step: number; tab: BookingTabParam } => ({
    step: clampStep(search.step),
    tab: bookingTab(search.tab),
  }),
  component: RosterShowEditorRoute,
});

const rosterShowPreviewRoute = createRoute({
  getParentRoute: () => rosterBookingRoute,
  path: "shows/$showId",
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: RosterShowPreviewRoute,
});

const rosterGuestsModalRoute = createRoute({
  getParentRoute: () => rosterBookingRoute,
  path: "shows/$showId/guests",
  validateSearch: (search: Record<string, unknown>): { tab: BookingTabParam } => ({
    tab: bookingTab(search.tab),
  }),
  component: RosterGuestsModalRoute,
});
// The SAME /assets page the band gets (PO, 2026-09-01), which is also what legacy did — its roster
// shell embedded the artist screens themselves. It used to mount `BrandScreen`, the Core cards without
// Link in Bio or EPK, so the two lenses were looking at different pages. `AssetsScreen` opens on
// `assets:read` and renders read-only without `assets:write`, which is how the Booking lens (the one
// root seeded `assets='read'`) reaches it at all.
const rosterAssetsRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "assets", component: AssetsScreen });
// Antenna under the roster lens (nav-parity CR) — the hub in its READ posture: a label seat holds
// antenna:read and a management seat antenna:manage, but the composer + manager modals are routed
// children of the ARTIST antenna route, so authoring is not reachable from here yet. `readOnly`
// makes that structural rather than leaving a management seat a button that opens nothing.
const rosterAntennaRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "antenna", component: RosterAntennaRoute });
// The Booking lens's phone `shows` tab — the SAME read-only shows list the band's `shows` tab opens (spec §3:
// "mobile books nothing"), with its per-show door and logistics screens, never the desktop cockpit.
const rosterShowsListRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "shows", component: RosterShowsListRoute });
const rosterDoorRoute = createRoute({ getParentRoute: () => rosterArtistRoute, path: "shows/$showId/guests", component: RosterDoorRoute });
const rosterShowLogisticsRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "shows/$showId/logistics",
  component: RosterShowLogisticsRoute,
});
const rosterReleasesRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "releases",
  // `?release=` — on a phone, the release the catalogue opens on. A single release's page redirects here below
  // `lg`, so every door onto a release (Needs attention, the release card, a notification) lands on the phone screen.
  validateSearch: (search: Record<string, unknown>): { release?: string } => ({
    release: typeof search.release === "string" && search.release !== "" ? search.release : undefined,
  }),
  component: RosterReleasesRoute,
});

// Roster release detail — the section rides the URL (`releases/$releaseId/$section`), deep-link/refresh safe like
// the artist side; the bare id redirects to Overview. One route + a section switch (vs the artist side's 8 child
// routes) keeps the roster mirror compact.
const rosterReleaseIndexRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "releases/$releaseId",
  beforeLoad: ({ params }) => {
    const p = params as { artistId: string; releaseId: string };
    throw redirect({ to: `/roster/artists/${p.artistId}/releases/${p.releaseId}/${DEFAULT_SECTION}` });
  },
});
const rosterReleaseSectionRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "releases/$releaseId/$section",
  component: RosterReleaseDetailRoute,
});

// Roster settings — the tab rides the URL (`settings/$tab`); the bare path redirects to Account.
const rosterSettingsIndexRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "settings",
  beforeLoad: ({ params }) => {
    throw redirect({ to: `/roster/artists/${(params as { artistId: string }).artistId}/settings/account` });
  },
});
const rosterSettingsTabRoute = createRoute({
  getParentRoute: () => rosterArtistRoute,
  path: "settings/$tab",
  component: RosterArtistSettingsRoute,
});

// The read-only Partner (Personal Viewer) shell — a pathless authed layout mounting PartnerShell
// (an EMPTY tab set). A seatless partner is redirected here from "/" (the account-root guard); they are
// server-confined to this surface, so this is the only place they can be. Anyone who is NOT a partner is sent
// home (product owner, 2026-10-08) — "/" then picks their own shell — rather than shown an empty partner view.
const partnerShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_partnerShell",
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    let me;
    try {
      me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
    } catch {
      return;   // fail open, like every guard here — the server fences the partner reads either way
    }
    if (!me.isPartnerViewer) throw redirect({ to: "/" });
  },
  component: PartnerShellLayout,
});

const partnerRoute = createRoute({
  getParentRoute: () => partnerShellRoute,
  path: "/partner",
  component: PartnerRoute,
});

// The partner view's event page (ART-239 part 2) — a ROUTE rather than an overlay, so the back swipe and browser
// Back work and no dialog is hand-rolled. Under `partner`, an already-reserved first segment: it reserves nothing.
// A SHOW opens at the band's own `/dos?show=` (product owner, 2026-10-08) — see DosRoute.
const partnerEventRoute = createRoute({
  getParentRoute: () => partnerShellRoute,
  path: "/partner/events/$eventId",
  component: PartnerEventRoute,
});

// The admin console (.design-specs/admin-console.plan.md) — a separate view sharing no screens with the band app
// (R8): its own pathless layout off the ROOT, outside `shellRoute`, so neither the band paywall nor any band
// provider applies. Admin accounts are DEDICATED (no seats, no other screens), so sign-in lands here and every
// app route sends them back (`sendAdminsToConsole`). Registered in every lane on purpose: the server is the gate
// (platform admin, and a second-factor session in production); a non-admin who types the URL gets the API's 403.
// `/admin` is a reserved band slug (905_reserve_admin_slug.sql, alb.tf, the box Caddyfile).
const adminShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_adminShell",
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    await sendNonAdminsHome(context);
  },
  component: AdminShellLayout,
});

const adminRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin",
  validateSearch: (search: Record<string, unknown>): { tab?: AdminTab; band?: string; id?: string } => ({
    ...(search.tab === undefined ? {} : { tab: parseAdminTab(search.tab) }),
    ...(typeof search.band === "string" && search.band ? { band: search.band } : {}),
    // The Applications tab's open application — what the new-application alert email links to.
    ...(typeof search.id === "string" && search.id ? { id: search.id } : {}),
  }),
  component: AdminRoute,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  welcomeRoute,
  tourRoute,
  forgotRoute,
  resetRoute,
  verifyEmailRoute,
  communicationPreferencesRoute,
  joinRoute,
  applyRoute,
  createOrgRoute,
  activateRoute,
  shellRoute.addChildren([
    dashboardRoute,
    bookingRoute.addChildren([guestsModalRoute, showNewRoute, showEditRoute, showPreviewRoute]),
    scheduleRoute,
    legacyScheduleRoute,
    doorRoute,
    dosRoute,
    showsListRoute,
    showVipRoute,
    showLogisticsRoute,
    roadCasesRoute,
    scanRoute,
    antennaRoute.addChildren([
      announcementNewRoute,
      announcementEditRoute,
      antennaSettingsRoute,
      antennaBackstageRoute,
      antennaCompsRoute,
    ]),
    releasesRoute.addChildren([releasesNewRoute]),
    releaseDetailRoute.addChildren([
      releaseIndexRoute,
      releaseOverviewRoute,
      releaseAssetsRoute,
      releaseTodoRoute,
      releaseSmartLinkRoute,
      releaseAdsRoute,
      releaseDetailsRoute,
      releaseTimelineRoute,
      releaseBudgetRoute,
    ]),
    contactsRoute,
    contactsBandRoute,
    contactsPersonRoute,
    musicRoute,
    // Spread, not a conditional child: an unregistered route is absent from routesById entirely, so there is
    // no half-mounted surface for a production user to reach.
    ...(IS_NON_PRODUCTION ? [debugRoute] : []),
    marketingRoute.addChildren([marketingCampaignNewRoute]),
    fansRoute.addChildren([broadcastNewRoute, broadcastStatsRoute]),
    contentRoute,
    contentPlatformRoute,
    assetsRoute.addChildren([assetsIndexRoute, assetsBrandRoute, assetsLinksRoute, assetsEpkRoute, assetsProductionRoute]),
    advanceRoute,
    busesRoute,
    messagesRoute,
    messageThreadRoute,
    notificationsRoute,
    profileRoute,
    settingsRoute.addChildren([settingsIndexRoute, settingsAccountRoute, settingsProfileRoute, settingsFeaturesRoute, settingsConnectionsRoute, settingsTeamRoute, settingsTutorialsRoute]),
  ]),
  rosterShellRoute.addChildren([
    rosterHomeRoute,
    rosterGateRoute,
    rosterTeamRoute,
    rosterMessagesRoute,
    rosterMessageThreadRoute,
    rosterSettingsRoute,
    rosterArtistRoute.addChildren([
      rosterArtistIndexRoute,
      rosterMusicRoute,
      rosterMarketingRoute,
      rosterContentRoute,
      rosterScheduleRoute,
      rosterLegacyScheduleRoute,
      rosterBookingRoute.addChildren([
        rosterShowNewRoute,
        rosterShowEditRoute,
        rosterShowPreviewRoute,
        rosterGuestsModalRoute,
      ]),
      rosterShowsListRoute,
      rosterDoorRoute,
      rosterShowLogisticsRoute,
      rosterAssetsRoute,
      rosterAntennaRoute,
      rosterReleasesRoute,
      rosterReleaseIndexRoute,
      rosterReleaseSectionRoute,
      rosterSettingsIndexRoute,
      rosterSettingsTabRoute,
    ]),
  ]),
  partnerShellRoute.addChildren([partnerRoute, partnerEventRoute]),
  adminShellRoute.addChildren([adminRoute]),
]);

/** Build the router with the runtime context (created once in Bootstrap). */
export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// ---- Route components (thin wrappers wiring navigation into the feature screens) -----------------------

function LoginRoute() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = useSearch({ from: loginRoute.id });
  return (
    <LoginScreen
      onAuthenticated={() => navigate({ to: redirectTo ?? "/" })}
      onForgotPassword={() => navigate({ to: "/forgot-password" })}
    />
  );
}

function WelcomeRoute() {
  const navigate = useNavigate();
  return (
    <WelcomeScreen
      onLearnMore={() => void navigate({ to: "/login/tour" })}
      onLogIn={() => void navigate({ to: "/login", search: { via: "welcome" } })}
    />
  );
}

function TourRoute() {
  const navigate = useNavigate();
  const { step } = useSearch({ from: tourRoute.id });
  return (
    <TourScreen
      step={step ?? 1}
      onStep={(next) => void navigate({ to: "/login/tour", search: { step: next }, replace: true })}
      onExit={() => void navigate({ to: "/login/welcome" })}
    />
  );
}

function ForgotRoute() {
  const navigate = useNavigate();
  return <ForgotPasswordScreen onBack={() => navigate({ to: "/login" })} />;
}

function ResetRoute() {
  const navigate = useNavigate();
  const { token, setup } = useSearch({ from: resetRoute.id });
  const auth = useAuth();
  const signedIn = auth.isSignedIn();
  const me = useMe({ enabled: signedIn });
  const signedInAs = signedIn ? (me.data?.person?.email ?? null) : null;
  return (
    <ResetPasswordScreen
      token={token ?? ""}
      setup={setup === true}
      signedInAs={signedInAs}
      onDone={() => navigate({ to: "/login" })}
    />
  );
}

function ApplyRoute() {
  const { mode, plan, from, storefront } = useSearch({ from: applyRoute.id });
  return (
    <ApplyPage
      initialMode={mode === "roster" ? "roster" : "artist"}
      initialProduct={plan === "pro" ? "pro" : "std"}
      hidePricing={hidesPricing(from, storefront)}
    />
  );
}

function JoinRoute() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: joinRoute.id });
  return (
    <JoinScreen
      token={token ?? ""}
      onJoined={() => navigate({ to: "/" })}
      onSignIn={() => navigate({ to: "/login", search: { redirect: `/join?token=${token ?? ""}` } })}
      onLeave={() => navigate({ to: "/login" })}
    />
  );
}

function CommunicationPreferencesRoute() {
  const navigate = useNavigate();
  const { token, unsubscribe } = useSearch({ from: communicationPreferencesRoute.id });
  return (
    <CommunicationPreferencesScreen token={token ?? ""} unsubscribe={unsubscribe} onDone={() => navigate({ to: "/" })} />
  );
}

function VerifyEmailRoute() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: verifyEmailRoute.id });
  return <VerifyEmailScreen token={token ?? ""} onDone={() => navigate({ to: "/login" })} />;
}

function DashboardRoute() {
  const phone = usePhoneLayout();
  const navigate = useNavigate();
  const { task } = useSearch({ from: dashboardRoute.id });
  // The routed task overlay: open a task (`?task=:id`), the create modal (`?task=new`), or close (drop the
  // param). `resetScroll: false` on BOTH: the router scrolls to the top of a new location by default, which is
  // right for going somewhere and wrong for opening a panel over where you already are — without it, clicking
  // a task halfway down the dashboard threw the page back to the top behind the drawer, and dropped you there
  // again on close.
  const openTask = (taskId: string) =>
    void navigate({ to: "/", search: { task: taskId }, resetScroll: false });
  const closeTask = () => void navigate({ to: "/", search: {}, resetScroll: false });
  // Home is shell-swapped: under the MobileShell the `/` route renders the role-keyed mobile HOME widget
  // stack; the desktop ArtistShell keeps the full dashboard grid. The seam is the shell's phone decision.
  if (phone) return <MobileHome />;
  return (
    <DashboardScreen
      openTask={task}
      onOpenTask={openTask}
      onNewTask={() => openTask("new")}
      onCloseTask={closeTask}
    />
  );
}

/**
 * The Booking page is for seats holding show money at the show grain (`hasBookingPage`); anyone else reaching
 * /booking by a link or a bookmark is sent to the Calendar their nav offers instead (ART-305).
 */
function BookingAccess({ children }: { children: ReactNode }) {
  const full = useCapability("money:full");
  const perShow = useCapability("money:per_show");
  if (!full && !perShow) return <Navigate to="/calendar" replace />;
  return <>{children}</>;
}

function BookingRoute() {
  const navigate = useNavigate();
  const { tab } = useSearch({ from: bookingRoute.id });
  return (
    <RequireBand>
      <BookingAccess>
      <BookingScreen
        // Every modal carries the CURRENT tab forward. A child route validates its own search, so whatever
        // it omits is dropped — without `tab` here, opening Add show on Holds silently reset the list behind
        // the scrim to Confirmed, and closing the modal landed you on the wrong tab (Ryan, 2026-08-23).
        //
        // `resetScroll: false` on all three, because all three now open a MODAL over this still-mounted list
        // (COMPONENTS.md, "A ROUTED overlay must pass `resetScroll: false`"). That covers `onOpenShow` as of
        // TASK-057: the preview used to be a full-screen DESTINATION, where scrolling to the top is right —
        // as an overlay it would instead throw the list behind the scrim to the top and drop you there again
        // on close.
        onAddShow={() =>
          void navigate({ to: "/booking/shows/new", search: { step: 1, tab }, resetScroll: false })
        }
        onOpenShow={(showId) =>
          void navigate({ to: `/booking/shows/${showId}`, search: { tab }, resetScroll: false })
        }
        onEditShow={(showId) =>
          void navigate({ to: `/booking/shows/${showId}/edit`, search: { step: 1, tab }, resetScroll: false })
        }
        tab={tab}
        // `replace`, so flicking between tabs does not stack history entries the Back button has to unwind.
        onTabChange={(next) => void navigate({ to: "/booking", search: { tab: next }, replace: true })}
      />
      {/* The routed overlays — the guest list, the show editor and the show preview — each draw their own
          fixed backdrop over this still-mounted list. */}
      <Outlet />
      </BookingAccess>
    </RequireBand>
  );
}

/**
 * ONE component behind BOTH editor URLs (`/booking/shows/new` and `/booking/shows/{id}/edit`).
 *
 * That is load-bearing, not tidiness. TanStack renders the matched child as `jsx(route.options.component)`
 * at a stable position under `<Outlet/>` with no key, so two DIFFERENT component functions are two element
 * types: creating a show would tear `RequireBand → ShowEditorModal → Modal` down and build a fresh one —
 * the `modal-in` animation replays, the header flashes back to "Untitled show", the wizard drops to its
 * loading state and `useModalA11y`'s mount focus-in yanks focus to the ✕. A visible hand-off, which is
 * exactly what "Add and Edit are one window" is supposed to rule out. Sharing the function keeps the element
 * type identical, so React reconciles in place and only the props change.
 *
 * Params/search are read loosely (`strict: false`) because the same component serves both routes; each route
 * still validates `step` in its own `validateSearch`.
 */
function ShowEditorRoute() {
  const navigate = useNavigate();
  const { showId } = useParams({ strict: false }) as { showId?: string };
  // `tab` rides every editor URL so the list behind the scrim keeps the tab it was opened from, and closing
  // returns to it. Read loosely like `step`, since one component serves both editor routes.
  const { step, tab: rawTab } = useSearch({ strict: false }) as { step?: number; tab?: unknown };
  const activeStep = clampStep(step);
  const tab = bookingTab(rawTab);
  const to = showId ? `/booking/shows/${showId}/edit` : "/booking/shows/new";
  const refreshLists = useRefreshBookingLists();
  return (
    <RequireBand>
      <ShowEditorModal
        showId={showId}
        step={activeStep}
        // `resetScroll: false` on every one of these: the editor is a MODAL over a still-mounted /booking
        // list, so stepping, the create URL swap, and the exit are all overlay moves — none of them should
        // throw the list behind the scrim to the top (COMPONENTS.md, "A ROUTED overlay must pass
        // resetScroll: false"). Exit especially: the reader gets their place in the list back.
        onStep={(s) => void navigate({ to, search: { step: s, tab }, resetScroll: false })}
        // `replace`, so creating a show swaps the URL UNDER an open modal: the sections unlock in place and
        // Add/Edit read as one window rather than a hand-off between two screens. Back still returns to
        // /booking rather than to a dead "new" URL.
        //
        // NB this no longer fires on a CLEAN create — that press dismisses to the list (PO, 2026-09-11;
        // `onSaved` below). It is reached when the editor stays open over a show that now exists: a partial
        // fan-out, or a failed confirm. Both rulings used to fire on one press and the dismiss won by
        // ordering alone; `ShowEditorModal.run` now decides it rather than the router.
        onCreated={(id) =>
          void navigate({
            to: `/booking/shows/${id}/edit`,
            search: { step: 1, tab },
            replace: true,
            resetScroll: false,
          })
        }
        onExit={() => void navigate({ to: "/booking", search: { tab }, resetScroll: false })}
        onDeleted={() => void navigate({ to: "/booking", search: { tab }, resetScroll: false })}
        // A clean save closes the editor AND refreshes the lists behind it (Ryan, 2026-08-23) — including
        // the save that CREATED the show (PO, 2026-09-11), so the reader lands back on the list with the new
        // hold on it rather than inside the editor. The fan-out
        // can change a show's date, venue or status, and only some of its sub-resource mutations invalidate
        // the LISTS on their own — so without this the modal closes onto a row still showing the old values,
        // which reads as the save having failed.
        onSaved={({ confirmed }) => {
          refreshLists();
          // A promoted hold has LEFT the Holds tab, so landing back on it would show the tab the show just
          // left — which reads as the confirm not having worked. Go where the show now is (Ryan,
          // 2026-08-23). A plain save does not move the show between tabs, so it keeps the current one.
          void navigate({
            to: "/booking",
            search: { tab: confirmed ? "confirmed" : tab },
            resetScroll: false,
          });
        }}
      />
    </RequireBand>
  );
}

function ShowPreviewRoute() {
  const navigate = useNavigate();
  const { showId } = useParams({ from: showPreviewRoute.id });
  const { tab } = useSearch({ from: showPreviewRoute.id });
  const refreshLists = useRefreshBookingLists();
  return (
    <RequireBand>
      <ShowPreview
        showId={showId}
        // Overlay → overlay, and since TASK-057 the preview is itself a modal over the same still-mounted
        // list, so neither hand-off may scroll it.
        onEdit={(id) =>
          void navigate({ to: `/booking/shows/${id}/edit`, search: { step: 1, tab }, resetScroll: false })
        }
        onOpenGuests={(id) =>
          void navigate({ to: `/booking/shows/${id}/guests`, search: { tab }, resetScroll: false })
        }
        // Closing the preview refreshes the lists too: settlement is edited HERE, and a settled figure moves
        // the Past tab's per-row money and the KPI strip above it.
        onBack={() => {
          refreshLists();
          void navigate({ to: "/booking", search: { tab }, resetScroll: false });
        }}
      />
    </RequireBand>
  );
}

function GuestsModalRoute() {
  const navigate = useNavigate();
  const { showId } = useParams({ from: guestsModalRoute.id });
  const { tab } = useSearch({ from: guestsModalRoute.id });
  return <GuestListModal showId={showId} onClose={() => void navigate({ to: "/booking", search: { tab } })} />;
}

function DoorRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { showId } = useParams({ from: doorRoute.id });
  return (
    <RequireBand>
      <DoorCheckInScreen
        showId={showId}
        // Return to wherever the door was opened FROM — the DOS essentials tile OR the booking screen's
        // door — rather than always /booking, which dumped a band/merch/production seat (whose mobile nav
        // has no booking surface) onto a desktop-only page (ART-153 pass two). `/dos` is the safe fallback
        // for a deep link with no history to step back through.
        onBack={() =>
          router.history.canGoBack() ? router.history.back() : void navigate({ to: "/dos" })
        }
      />
    </RequireBand>
  );
}

function DosRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { show, via, band } = useSearch({ from: dosRoute.id });
  const queryClient = useQueryClient();
  const activeBandId = useResolvedActiveBandId();
  const setActiveBand = useActiveBand((s) => s.setActiveBand);
  const me = useMe().data;
  const seats = me?.seats;

  // A Live Activity tap names its band. Switch to it (the one audited scorch path) when it is another of the
  // person's bands, then drop `band` so the URL is the ordinary `/dos?show=`. A band they hold no seat on is ignored.
  useEffect(() => {
    if (!band || !seats) return;
    const holds = seats.some((seat) => seat.kind === "band" && seat.bandId === band);
    void (async () => {
      if (holds && band !== activeBandId) {
        await switchBand(band, { queryClient, realtime: null, navigate: (id) => setActiveBand(id) });
      }
      void navigate({ to: "/dos", search: { show }, replace: true });
    })();
    // activeBandId deliberately excluded: the switch itself changes it, and must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band, seats]);

  if (me?.isPartnerViewer) return <PartnerDosRoute showId={show} />;
  if (band) return null;
  return (
    <DayOfShowScreen
      initialShowId={show}
      // Opened by a link from another screen: back returns there (a history-back, so the iOS swipe agrees).
      onLeave={via === "link" && router.history.canGoBack() ? () => router.history.back() : undefined}
      // Reflect the open show in the URL (replace, so picks don't stack history) — the door/VIP/logistics
      // screens history-back to `/dos?show=<id>`, which reopens it.
      onShowChange={(id) => void navigate({ to: "/dos", search: { show: id ?? undefined }, replace: true })}
      onOpenGuests={(showId) => void navigate({ to: `/shows/${showId}/guests` })}
      onOpenVip={(showId) => void navigate({ to: `/shows/${showId}/vip` })}
      onOpenLogistics={(showId) => void navigate({ to: `/shows/${showId}/logistics` })}
    />
  );
}

function ScheduleRoute() {
  const { date, show } = useSearch({ from: scheduleRoute.id });
  return <OpsCalendarScreen initialDate={date} initialShowId={show} />;
}

function ShowsListRoute() {
  const navigate = useNavigate();
  return (
    <MobileShowsScreen
      onOpenGuests={(showId) => void navigate({ to: "/shows/$showId/guests", params: { showId } })}
      onOpenLogistics={(showId) => void navigate({ to: "/shows/$showId/logistics", params: { showId } })}
    />
  );
}

function ShowVipRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { showId } = useParams({ from: showVipRoute.id });
  // Return to origin — the DOS detail of the show it was opened from (`/dos?show=<id>`), which history-back
  // restores. Was a hardcoded `/dos`, which re-resolved to auto/list and lost the picked show (ART-153 pass
  // two). `/dos` is the safe fallback for a deep link with no history.
  return (
    <ShowVipScreen
      showId={showId}
      onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/dos" }))}
    />
  );
}

function ScanRoute() {
  const { token } = useSearch({ from: scanRoute.id });
  return <ScanScreen token={token} />;
}

function ShowLogisticsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { showId } = useParams({ from: showLogisticsRoute.id });
  // Logistics is reached from BOTH the DOS essentials AND the mobile shows list, so return to origin rather
  // than always /dos (ART-153 pass two). /dos is the safe fallback for a deep link with no history.
  return (
    <ShowLogisticsScreen
      showId={showId}
      onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/dos" }))}
    />
  );
}

function AntennaRoute() {
  const navigate = useNavigate();
  return (
    <RequireBand>
      {/* The launch gate (AC-1) wraps the hub AND the outlet: a closed band's deep link to a routed modal
          renders the not-open state, never the modal. */}
      <AntennaHubGate>
        <AntennaHubScreen
          onNewAnnouncement={() => void navigate({ to: "/antenna/announcements/new" })}
          onEditAnnouncement={(id) => void navigate({ to: `/antenna/announcements/${id}/edit` })}
          onOpenSettings={() => void navigate({ to: "/antenna/settings" })}
          onOpenBackstage={() => void navigate({ to: "/antenna/backstage" })}
          onOpenComps={() => void navigate({ to: "/antenna/comps" })}
        />
        {/* The routed composer + management modals overlay the hub via their own fixed backdrops. */}
        <Outlet />
      </AntennaHubGate>
    </RequireBand>
  );
}

/**
 * The roster Antenna tab. Read-only by construction — see `rosterAntennaRoute`. No `RequireBand`
 * wrapper: the band comes from the RosterBandProvider the artist layout already mounts, not from the
 * active-band store the artist shell uses.
 */
function RosterAntennaRoute() {
  // The tab is keyed on the subview (strip-shape stability), so it is present for a closed artist too; the
  // gate turns that into the designed "not open for this artist yet" page instead of a hub of 403s.
  return (
    <AntennaHubGate>
      <AntennaHubScreen
        readOnly
        onNewAnnouncement={() => {}}
        onEditAnnouncement={() => {}}
      />
    </AntennaHubGate>
  );
}

function AnnouncementNewRoute() {
  const navigate = useNavigate();
  return <AnnouncementComposerModal onClose={() => void navigate({ to: "/antenna" })} />;
}

function AnnouncementEditRoute() {
  const navigate = useNavigate();
  const { id } = useParams({ from: announcementEditRoute.id });
  return (
    <AnnouncementComposerModal announcementId={id} onClose={() => void navigate({ to: "/antenna" })} />
  );
}

function AntennaSettingsRoute() {
  const navigate = useNavigate();
  return <FanClubSettingsModal onClose={() => void navigate({ to: "/antenna" })} />;
}

function AntennaBackstageRoute() {
  const navigate = useNavigate();
  return (
    <BackstageManagerModal
      onClose={() => void navigate({ to: "/antenna" })}
      onOpenSettings={() => void navigate({ to: "/antenna/settings" })}
    />
  );
}

function AntennaCompsRoute() {
  const navigate = useNavigate();
  return <CompsManagerModal onClose={() => void navigate({ to: "/antenna" })} />;
}

function ContactsRoute() {
  const navigate = useNavigate();
  // Desktop opens the band/person profile as a modal (from the cards' own state); mobile navigates to the
  // dedicated screens. Passing the door callbacks ONLY on mobile keeps the modal path — and the cards'
  // no-RouterProvider tests — unchanged, and keeps `useNavigate` in the wrapper, not the cards.
  const isDesktop = !usePhoneLayout();
  const onOpenBand = isDesktop
    ? undefined
    : (bandId: string) => void navigate({ to: "/contacts/band/$bandId", params: { bandId } });
  // A person opens as the profile SHEET over the list (ART-264), the same door every other surface uses.
  const onOpenPerson = isDesktop ? undefined : (personId: string) => openPersonProfile(personId);
  return (
    <RequireBand>
      {isDesktop ? (
        <ContactsScreen onOpenBand={onOpenBand} onOpenPerson={onOpenPerson} />
      ) : (
        // ART-193: the phone gets a dedicated single-scroll screen (friends rail + crew list), not the
        // desktop four-card grid. Same door callbacks — taps route to the /contacts/person|band screens.
        <MobileContactsScreen onOpenBand={onOpenBand} onOpenPerson={onOpenPerson} />
      )}
    </RequireBand>
  );
}

function ContactsBandRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { bandId } = useParams({ from: contactsBandRoute.id });
  // The profile screen is mobile-only; a desktop deep-link bounces to /contacts (the `ReleasesNewRoute`
  // pattern), where the modal is the desktop affordance.
  const isDesktop = !usePhoneLayout();
  if (isDesktop) return <Navigate to="/contacts" replace />;
  return (
    <RequireBand>
      <MobileBandProfileScreen
        bandId={bandId}
        onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/contacts" }))}
        onOpenFriend={(id) => void navigate({ to: "/contacts/band/$bandId", params: { bandId: id } })}
        onOpenMember={(personId) => openPersonProfile(personId)}
      />
    </RequireBand>
  );
}

function ContactsPersonRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { personId } = useParams({ from: contactsPersonRoute.id });
  // Mobile-only, like the band profile: a desktop deep-link bounces to /contacts (the modal is desktop's).
  const isDesktop = !usePhoneLayout();
  if (isDesktop) return <Navigate to="/contacts" replace />;
  return (
    <RequireBand>
      <MobileContactProfileScreen
        personId={personId}
        onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/contacts" }))}
        onOpenBand={(bandId) => void navigate({ to: "/contacts/band/$bandId", params: { bandId } })}
      />
    </RequireBand>
  );
}

function MusicRoute() {
  return (
    <RequireBand>
      <MusicScreen />
    </RequireBand>
  );
}

function DebugRoute() {
  const { tab } = debugRoute.useSearch();
  const navigate = useNavigate();
  return (
    <RequireBand>
      <DebugScreen
        activeTab={parseDebugTab(tab)}
        onTab={(next) => void navigate({ to: "/debug", search: { tab: next }, replace: true })}
      />
    </RequireBand>
  );
}

function MarketingRoute() {
  return (
    <RequireBand>
      <MarketingScreen />
      {/* The routed Ad Creator overlays the marketing surface via its own fixed backdrop. */}
      <Outlet />
    </RequireBand>
  );
}

function MarketingCampaignNewRoute() {
  const navigate = useNavigate();
  const { release, dest } = useSearch({ from: marketingCampaignNewRoute.id });
  return (
    <AdCreatorModal
      initialReleaseId={release}
      initialDestinationUrl={dest}
      onClose={() => void navigate({ to: "/marketing" })}
      onPushed={() => void navigate({ to: "/marketing" })}
    />
  );
}

function FansRoute() {
  return (
    <RequireBand>
      <FansScreen />
      {/* The routed composer + per-broadcast stats modals overlay the fans screen via their own backdrops. */}
      <Outlet />
    </RequireBand>
  );
}

function BroadcastNewRoute() {
  const navigate = useNavigate();
  return (
    <BroadcastComposerModal
      onClose={() => void navigate({ to: "/marketing/fans" })}
      onLogged={() => void navigate({ to: "/marketing/fans" })}
    />
  );
}

function BroadcastStatsRoute() {
  const navigate = useNavigate();
  const { broadcastId } = useParams({ from: broadcastStatsRoute.id });
  return <BroadcastStatsModal broadcastId={broadcastId} onClose={() => void navigate({ to: "/marketing/fans" })} />;
}

function ContentRoute() {
  return (
    <RequireBand>
      <ContentScreen />
    </RequireBand>
  );
}

function ContentPlatformRoute() {
  const navigate = useNavigate();
  const { platform } = useParams({ from: contentPlatformRoute.id });
  return (
    <RequireBand>
      <ContentPlatformScreen platform={platform} onBack={() => void navigate({ to: "/socials" })} />
    </RequireBand>
  );
}

// ---- Releases ------------------------------------------------------------------------

/** The releaseId param, read by every section child from the detail route that declares it. */
function useDetailReleaseId(): string {
  return useParams({ from: releaseDetailRoute.id }).releaseId;
}

function ReleasesRoute() {
  const navigate = useNavigate();
  const { release } = useSearch({ from: releasesRoute.id });
  // Below `lg` the hub is the ART-189 mobile screen — a swipeable catalogue with the selected release's stats
  // read in place, read-only. At/above it, the desktop hub. The two never mount together.
  const isDesktop = !usePhoneLayout();
  return (
    <RequireBand>
      {isDesktop ? (
        <ReleasesScreen
          onNewRelease={() => void navigate({ to: "/releases/new" })}
          onOpenRelease={(releaseId, section) =>
            void navigate({ to: `/releases/${releaseId}/${section ?? DEFAULT_SECTION}` })
          }
        />
      ) : (
        <MobileReleasesScreen initialReleaseId={release} />
      )}
      {/* The routed new-release type picker overlays the hub via its own fixed backdrop (desktop only — the
          create route redirects a phone back to /releases). */}
      <Outlet />
    </RequireBand>
  );
}

function ReleasesNewRoute() {
  const navigate = useNavigate();
  // Creating a release is an editor action — desktop-only (spec §3, ART-153 pass two). The "New release"
  // affordance is already hidden below `lg`; this closes the deep-link so a phone can't open the create modal.
  const isDesktop = !usePhoneLayout();
  if (!isDesktop) return <Navigate to="/releases" replace />;
  return (
    <NewReleaseModal
      onClose={() => void navigate({ to: "/releases" })}
      onCreated={(releaseId) => void navigate({ to: `/releases/${releaseId}/${DEFAULT_SECTION}` })}
    />
  );
}

function ReleaseDetailRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const location = useLocation();
  const { releaseId } = useParams({ from: releaseDetailRoute.id });
  const activeSection = sectionFromPath(location.pathname);
  // A phone has no single-release page: the releases screen IS the per-release read, opened on this release.
  const phone = usePhoneLayout();
  if (phone) return <Navigate to="/releases" search={{ release: releaseId }} replace />;
  return (
    <RequireBand>
      <ReleaseDetailScreen
        releaseId={releaseId}
        activeSection={activeSection}
        // A section switch REPLACES the history entry (ART-268): moving around inside one release is not a step
        // back should retrace, and the mobile back (chevron and iOS edge swipe alike) is a history-back.
        onSection={(key) => void navigate({ to: `/releases/${releaseId}/${key}`, resetScroll: false, replace: true })}
        onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/releases" }))}
      >
        {/* The active numbered section renders through the routed Outlet. */}
        <Outlet />
      </ReleaseDetailScreen>
    </RequireBand>
  );
}

/** Derive the active section from the URL's last segment (deep-link/refresh safe); default to Overview. */
function sectionFromPath(pathname: string): ReleaseSectionKey {
  const last = pathname.split("/").filter(Boolean).pop();
  return RELEASE_SECTIONS.some((s) => s.key === last) ? (last as ReleaseSectionKey) : DEFAULT_SECTION;
}

// Named section-route components (rules-of-hooks: each reads the releaseId param then renders its section).
function OverviewSectionRoute() { return <OverviewSection releaseId={useDetailReleaseId()} />; }
function AssetsSectionRoute() { return <AssetsSection releaseId={useDetailReleaseId()} />; }
function ToDoSectionRoute() { return <ToDoSection releaseId={useDetailReleaseId()} />; }
function SmartLinkSectionRoute() { return <SmartLinkSection releaseId={useDetailReleaseId()} />; }
function LiveAdsSectionRoute() { return <LiveAdsSection releaseId={useDetailReleaseId()} />; }
function DetailsSectionRoute() { return <DetailsSection releaseId={useDetailReleaseId()} />; }
function TimelineSectionRoute() { return <TimelineSection releaseId={useDetailReleaseId()} />; }
function BudgetSectionRoute() { return <BudgetSection releaseId={useDetailReleaseId()} />; }

/**
 * Close a ROUTED inbox page. It used to be `navigate({ to: "/" })`, which is the second half of the
 * complaint the overlay fixed (PO, 2026-09-11): pressing the envelope cost you the page you were on, and
 * closing it dropped you on the dashboard rather than back where you started — two ways to lose your place
 * from one control.
 *
 * The envelope no longer routes here at all; what still lands on these pages is a notification tap, a
 * restored tab, and mobile's own tab bar. For the first two, BACK is the page they came from. For a cold
 * open — a fresh tab on `/messages/{id}`, where there is no history to return to — back would leave the app,
 * so the dashboard is the floor.
 */
function useCloseRoutedInbox(fallback: string) {
  const router = useRouter();
  const navigate = useNavigate();
  return () => {
    if (router.history.canGoBack()) router.history.back();
    else void navigate({ to: fallback });
  };
}

function MessagesRoute() {
  const navigate = useNavigate();
  const close = useCloseRoutedInbox("/");
  const { with: withPersonId } = messagesRoute.useSearch();
  const openedThread = useRef(false);
  return (
    <InboxScreen
      onSelectThread={(threadId) => {
        openedThread.current = true;
        void navigate({ to: `/messages/${threadId}` });
      }}
      onClose={close}
      routed
      openWithPersonId={withPersonId}
      // Drop `?with=` once taken, so a refresh does not re-ask — but NOT when the request opened an existing
      // conversation: that navigation already left this URL, and a replace here would land back on the list.
      onOpenedWithPerson={() => {
        if (!openedThread.current) void navigate({ to: "/messages", search: {}, replace: true });
      }}
    />
  );
}

function MessageThreadRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const close = useCloseRoutedInbox("/");
  const { threadId } = useParams({ from: messageThreadRoute.id });
  const back = () => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/messages" }));
  useChatSwipeBack(back);
  // The PAGE is inside the shell's BandProvider (where the topbar overlay is not), so the mounted band is
  // readable here — the same rule either way, from whichever source that surface has.
  const mounted = useBandContextOrNull()?.bandId;
  const showHref = useArtistShowLink(mounted);
  return (
    <InboxScreen
      selectedThreadId={threadId}
      onSelectThread={(id) => void navigate({ to: `/messages/${id}` })}
      onBack={back}
      showHref={showHref}
      onNavigate={(route) => void navigate({ to: route })}
      // C-Esc's FIRST layer. The selection rides the URL here, so "close the conversation" is a navigation
      // back to the list — the same place `onBack` goes, reached by a different gesture.
      onCloseThread={() => void navigate({ to: "/messages" })}
      onClose={close}
      routed
    />
  );
}

/**
 * An open chat on a PHONE registers its back like any drilled-in screen (ART-268), so the iOS edge swipe works there
 * too. The takeover hides the shell header, so nothing draws a second chevron — the registration only turns the
 * swipe on. The chat's own back is the same history-back, so the swipe and its chevron agree. Desktop registers
 * nothing: the inbox there is an overlay, not a drill-in.
 */
function useChatSwipeBack(back: () => void) {
  const isDesktop = !usePhoneLayout();
  useMobileBack(isDesktop ? null : back);
}

function RosterMessagesRoute() {
  const navigate = useNavigate();
  const close = useCloseRoutedInbox("/roster");
  const { with: withPersonId } = rosterMessagesRoute.useSearch();
  const openedThread = useRef(false);
  return (
    <InboxScreen
      onSelectThread={(threadId) => {
        openedThread.current = true;
        void navigate({ to: `/roster/messages/${threadId}` });
      }}
      onClose={close}
      routed
      openWithPersonId={withPersonId}
      // Drop `?with=` once taken, so a refresh does not re-ask — but NOT when the request opened an existing
      // conversation: that navigation already left this URL, and a replace here would land back on the list.
      onOpenedWithPerson={() => {
        if (!openedThread.current) void navigate({ to: "/roster/messages", search: {}, replace: true });
      }}
    />
  );
}

function RosterMessageThreadRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const close = useCloseRoutedInbox("/roster");
  const { threadId } = useParams({ from: rosterMessageThreadRoute.id });
  const back = () => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/roster/messages" }));
  useChatSwipeBack(back);
  const showHref = useRosterShowLink();
  return (
    <InboxScreen
      selectedThreadId={threadId}
      onSelectThread={(id) => void navigate({ to: `/roster/messages/${id}` })}
      onBack={back}
      showHref={showHref}
      onNavigate={(route) => void navigate({ to: route })}
      // C-Esc's FIRST layer. The selection rides the URL here, so "close the conversation" is a navigation
      // back to the list — the same place `onBack` goes, reached by a different gesture.
      onCloseThread={() => void navigate({ to: "/roster/messages" })}
      onClose={close}
      routed
    />
  );
}

function NotificationsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  return (
    <NotificationsScreen
      onNavigate={(route) => void navigate({ to: route })}
      // Reached from the header bell on ANY tab, so back returns to ORIGIN (the tab you were on) rather than
      // jumping to the dashboard — the bottom-nav tabs are the roots of back-nav (ART-153 follow-up). `/` is
      // the fallback only for a cold deep-link with no history to step back through.
      onBack={() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/" }))}
    />
  );
}

function ProfileRoute() {
  const navigate = useNavigate();
  const { person, mode } = useSearch({ from: profileRoute.id });
  // Person-scoped modal — closing returns to the dashboard (it can be opened from anywhere).
  return <ProfileModal personId={person} mode={mode} onClose={() => void navigate({ to: "/" })} />;
}

function AssetsRoute() {
  return (
    <RequireBand>
      <Outlet />
    </RequireBand>
  );
}

function AssetsIndexRoute() {
  const navigate = useNavigate();
  return <AssetsScreen onNavigate={(to) => void navigate({ to })} />;
}

function AdvanceRoute() {
  // Standalone page — no AssetsNav (Advance Docs is not an Assets sub-surface).
  return (
    <RequireBand>
      <ProductionScreen />
    </RequireBand>
  );
}

function BusesRoute() {
  // The phone gets the narrow ROAD editor (ART-148 — bunks, driver, codes, Wi-Fi), not the desktop drag-and-drop
  // fleet builder; the fleet is still structured on desktop. Reached from the Pro More row.
  const isDesktop = !usePhoneLayout();
  return <RequireBand>{isDesktop ? <BusesScreen /> : <MobileBusesScreen />}</RequireBand>;
}

function SettingsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const location = useLocation();
  // Settings is reached from the account menu (a drill-in from chrome), so the mobile header's leading slot
  // is a back, not the logo (PO, 2026-09-14). Register a history-back with a home fallback; on desktop this
  // is a no-op (only the mobile header consumes it).
  useMobileBack(() => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/" })));
  const activeTab: SettingsTab = location.pathname.endsWith("/tutorials")
    ? "tutorials"
    : location.pathname.endsWith("/features")
    ? "features"
    : location.pathname.endsWith("/connections")
      ? "connections"
      : location.pathname.endsWith("/team")
      ? "team"
      : location.pathname.endsWith("/profile")
        ? "profile"
        : "account";
  return (
    <RequireBand>
      {/* `resetScroll: false`: switching tabs keeps the reader's scroll position rather than jumping to the top
          (the artist shell scrolls the window, so the router's default reset would fire). */}
      <SettingsScreen activeTab={activeTab} onTab={(tab) => void navigate({ to: `/settings/${tab}`, resetScroll: false, replace: true })}>
        {/* The active tab renders through the routed Outlet (SettingsAccountTab | SettingsProfileTab | SettingsConnectionsTab). */}
        <Outlet />
      </SettingsScreen>
    </RequireBand>
  );
}

function SettingsAccountRoute() {
  return <SettingsAccountTab />;
}

/**
 * The two band-wide tabs are AV-owner only (`band:manage`). Hiding the tab is not enough — these are real
 * URLs, so a seat without the capability that deep-links (or lands here from the OAuth return) must be sent
 * to a tab it can actually see rather than shown a panel with no tab selected. The write endpoints behind
 * both are capability-gated server-side; this is the door, not the lock.
 */
function BandManageOnly({ children }: { children: ReactNode }) {
  const canManageBand = useCapability("band:manage");
  const navigate = useNavigate();
  useEffect(() => {
    if (!canManageBand) void navigate({ to: "/settings/account", replace: true });
  }, [canManageBand, navigate]);
  return canManageBand ? <>{children}</> : null;
}

function SettingsProfileRoute() {
  return (
    <BandManageOnly>
      <SettingsProfileTab />
    </BandManageOnly>
  );
}

function SettingsFeaturesRoute() {
  // The Features tab is hidden for now (settingsTabs.ts), so a deep link lands on Account rather than a tab
  // with no strip entry. Restore `<SettingsFeaturesTab />` when the tab comes back.
  return <Navigate to="/settings/account" replace />;
}

function SettingsConnectionsRoute() {
  return (
    <BandManageOnly>
      <SettingsConnectionsTab />
    </BandManageOnly>
  );
}

function SettingsTeamRoute() {
  return (
    <BandManageOnly>
      <SettingsTeamTab />
    </BandManageOnly>
  );
}

/**
 * The org-home, forked on the SERVER-derived roster subview (`useResolvedOrg.subview` — the org seat's
 * `root`, never a client role matrix). A 3-way switch, not a boolean, so slots the Booking home into the
 * `booking` arm: Management → the "Roster heartbeat"; Booking → the "Booking pipeline"; Label /
 * default → the composed Label overview.
 */
function RosterHomeRoute() {
  const navigate = useNavigate();
  const { subview, orgId } = useResolvedOrg();
  const onNavigate = (route: string) => void navigate({ to: route });
  // A cross-roster to-do row opens the TASK, on its own artist's landing page under this lens — the
  // `?task=:id` overlay `rosterArtistIndexRoute` now validates (ART-119). It is a separate callback rather
  // than a query string smuggled through `onNavigate`, because that one takes a bare path and the router
  // would not parse a search out of it.
  const onOpenTask = (bandId: string, taskId: string) =>
    void navigate({ to: `/roster/artists/${bandId}`, search: { task: taskId } });
  // ART-358 / ART-361 / ART-360: below `lg` (the breakpoint the mobile roster shell swaps on) every roster lens
  // is ONE artist — the one viewed last, else the roster's first by name. There is no All-roster view on a phone,
  // so this route only hands off to that artist.
  const wide = !usePhoneLayout();
  const artists = useRosterArtists(!wide ? orgId : undefined);
  if (!wide) {
    if (artists.isLoading || !orgId) return <RosterMobileHomeLoading />;
    const id = resolveRosterArtistId(orgId, artists.data ?? []);
    if (id) return <Navigate to="/roster/artists/$artistId" params={{ artistId: id }} replace />;
    return (
      <EmptyState
        title="No artists yet"
        description="Artists you're assigned to appear here once you join them by invite."
      />
    );
  }
  switch (subview) {
    case "management":
      // No header action here (PO, 2026-09-02): task create is band-scoped, so the cross-roster page has
      // nothing to create on. "+ Add task" lives on the per-artist heartbeat, where the band is known.
      return <RosterHeartbeatScreen onNavigate={onNavigate} onOpenTask={onOpenTask} />;
    case "booking":
      // The Booking pipeline owns its own header, states and section order (2026-09-01) — it is no longer
      // the Label home with a booking block spliced into the middle of it. "+ New offer" (ART-139) is
      // handled ENTIRELY within the screen — pick a band, create in place — so it needs nothing from the route.
      return <BookingRosterScreen onNavigate={onNavigate} />;
    default:
      return <RosterHomeScreen onNavigate={onNavigate} onOpenTask={onOpenTask} />;
  }
}

/** The phone roster home while the artist list resolves, before it hands off to the remembered artist. */
function RosterMobileHomeLoading() {
  return (
    <div role="status" aria-live="polite" style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
      <Spinner size={22} label="Loading your roster…" />
    </div>
  );
}

/** The paywall wrapper (P-08) — resolves the active band from /me and wires navigation + logout.*/
function ActivateRoute() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { checkout } = useSearch({ from: activateRoute.id });
  const me = useMe();
  const bandId = me.data?.activeBandId ?? me.data?.seats?.find((s) => s.kind === "band")?.bandId ?? undefined;
  const bandName = me.data?.seats?.find((s) => s.kind === "band" && s.bandId === bandId)?.displayName ?? undefined;
  async function backToLogin() {
    await auth.logout();
    await navigate({ to: "/login" });
  }
  return (
    <ActivateScreen
      bandId={bandId ?? undefined}
      bandName={bandName}
      checkoutReturn={checkout}
      onActivated={() => void navigate({ to: "/" })}
      onBackToLogin={() => void backToLogin()}
    />
  );
}

/** Parse the roster URL into the focused artist + active module tab (drives the tab strip highlight + scope). */
function parseRosterLocation(pathname: string, subview?: string): { focusedArtistId?: string; activeTab?: string } {
  const segs = pathname.split("/").filter(Boolean); // ['roster','artists','ID','releases',…]
  if (segs[0] !== "roster") return {};
  // Segment → KEY: the strip highlights by key, and /booking is the `schedule` key's segment.
  if (segs[1] === "artists" && segs[2])
    return { focusedArtistId: segs[2], activeTab: segs[3] ? rosterTabKeyForSegment(segs[3], subview) : "dashboard" };
  if (segs[1] === "g" && segs[2]) return { activeTab: segs[2] };
  // Messages is a bottom-tab destination on mobile — light the "inbox" tab (the desktop nav has no such
  // item, so this matches nothing there, which is correct: inbox is the topbar button on desktop).
  if (segs[1] === "messages") return { activeTab: "inbox" };
  if (segs.length === 1) return { activeTab: "dashboard" }; // /roster (All-roster)
  return {}; // /roster/team, /roster/settings — org-level, no artist tab active
}

/**
 * The RosterShell layout — mounts RealtimeProvider (org + band groups + the scorch's group
 * leave) around the RosterShell chrome. Derives the focused artist + active tab from the URL and wires the tab
 * strip + rail. A rail artist switch runs the switchRosterArtist SCORCH (the close).
 */
function RosterShellLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const realtime = useRealtimeOrNull();
  // This device hears the signed-in person's notifications, even for a manager who never opens an artist (ART-259).
  usePushRegistration();
  // Someone has signed in on this phone, so its signed-out screen is Log in from now on, not Welcome (ART-346).
  useEffect(() => void markSignedInOnThisDevice(), []);
  // …and carries the DOS lock-screen card for their shows, on every band, with no button (ART-208).
  useLiveActivitySync();
  // The roster's phone/desktop decision — the same rule as the band shell (`app/phoneLayout.ts`), published to
  // RosterShell (which swaps its chrome on it) and every roster screen. A phone has no inbox overlay.
  const rosterPhone = useShellPhoneDecision();
  // The subview picks the Touring SEGMENT (Booking's /booking vs Label's /calendar), the same server-derived
  // value that already picks its LABEL — so the URL and the tab can never name different pages.
  const { subview } = useResolvedOrg();
  const { focusedArtistId, activeTab } = parseRosterLocation(location.pathname, subview);
  const { orgId: layoutOrgId } = useResolvedOrg();
  const rosterArtists = useRosterArtists(layoutOrgId);
  // The phone dashboards open on the artist viewed last (ART-358 / ART-361), so every artist opened is remembered.
  useEffect(() => {
    if (layoutOrgId && focusedArtistId) rememberRosterArtist(layoutOrgId, focusedArtistId);
  }, [layoutOrgId, focusedArtistId]);

  async function onLogout() {
    await auth.logout();
    await navigate({ to: "/login" });
  }

  async function onSelectArtist(id: string | null) {
    if (id === null) {
      await navigate({ to: "/roster" }); // All-roster (no per-artist scorch needed — same tenant on return)
      return;
    }
    if (id === focusedArtistId) return;
    // The SCORCH: evict the departing artist's cache + in-flight + band group before B mounts, so B
    // renders zero carried values (the close). The person + org:{orgId} groups persist.
    await switchRosterArtist(focusedArtistId, id, {
      queryClient,
      leaveBandGroup: realtime ? (bandId) => realtime.leave(`band:${bandId}`) : undefined,
      navigate: (bandId) => navigate({ to: `/roster/artists/${bandId}` }),
    });
  }

  function onTab(tab: string) {
    // On a phone the single-artist lenses never sit on All roster: a tab pressed from an org-level page (the
    // inbox, say) opens on the artist viewed last.
    const artistId = focusedArtistId
      ?? (rosterPhone && isSingleArtistMobileLens(subview)
        ? resolveRosterArtistId(layoutOrgId, rosterArtists.data ?? [])
        : undefined);
    if (artistId) {
      void navigate({
        to: tab === "dashboard"
          ? `/roster/artists/${artistId}`
          // The KEY is server data; the SEGMENT is ours (schedule ⇒ /booking on the money lenses).
          : `/roster/artists/${artistId}/${rosterTabRoute(tab, subview)}`,
      });
    } else {
      void navigate({ to: tab === "dashboard" ? "/roster" : `/roster/g/${tab}` });
    }
  }

  return (
    <RealtimeProvider>
      <PhoneLayoutProvider phone={rosterPhone}>
      <RosterShell
        focusedArtistId={focusedArtistId}
        activeTab={activeTab}
        onSelectArtist={(id) => void onSelectArtist(id)}
        onTab={onTab}
        onNavigate={(route) => void navigate({ to: route })}
        onLogout={() => void onLogout()}
      >
        <Outlet />
      </RosterShell>
      {/* The roster has no band-profile page to open from a profile's band tile, so the tiles stay facts. */}
      <ProfileHost pathname={location.pathname} />
      <MessageRequestBridge active={rosterPhone} onRequest={(personId) => void navigate({ to: "/roster/messages", search: { with: personId } })} />
      <ToastHost />
      </PhoneLayoutProvider>
    </RealtimeProvider>
  );
}

/** The artist-required gate for a scoped tab opened with no artist selected (`/roster/g/$tab`). */
function RosterGateRoute() {
  const navigate = useNavigate();
  const { tab } = useParams({ from: rosterGateRoute.id });
  const { type } = useSearch({ from: rosterGateRoute.id });
  const { subview } = useResolvedOrg();
  // Mounted under the shell's OrgProvider, so the org-home capability set is readable here.
  const canManageTeam = useOrgCapability("roster:manage_seats");
  // The Booking lens's Calendar needs no artist: with none picked it is the whole roster's deals (ART-331).
  if (tab === "calendar" && subview === "booking") {
    return (
      <BookingCalendarScreen
        scope="roster"
        type={type}
        onTypeChange={(t) =>
          void navigate({ to: "/roster/g/calendar", search: t ? { type: t } : {}, replace: true, resetScroll: false })}
      />
    );
  }
  return (
    <ArtistRequiredGate
      tabKey={tab}
      onPick={(artistId) =>
        void navigate({
          to: tab === "dashboard"
            ? `/roster/artists/${artistId}`
            : `/roster/artists/${artistId}/${rosterTabRoute(tab, subview)}`,
        })
      }
      onManageTeam={canManageTeam ? openTeamPanel : undefined}
    />
  );
}

/** The per-artist layout — the org-lens BandProvider around the routed module Outlet. */
function RosterArtistLayout() {
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  return (
    <RosterBandProvider artistId={artistId}>
      <Outlet />
    </RosterBandProvider>
  );
}

/**
 * The per-artist landing, subview-dispatched on the SERVER-derived org seat root (`useResolvedOrg().subview` —
 * never a client role matrix). A switch, not a boolean: Management → the money-bearing heartbeat;
 * Booking → the money-bearing Booking cockpit; Label (and any other) → the money-free 4-card.
 * Mgmt/Booking hold money:lifetime, so their money surfaces self-gate; the Label shape stays structurally money-free.
 */
function RosterArtistDashboardRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { task } = useSearch({ from: rosterArtistIndexRoute.id });
  const { subview } = useResolvedOrg();
  const onOpenTab = (tab: string) =>
    void navigate({ to: `/roster/artists/${artistId}/${rosterTabRoute(tab, subview)}` });
  // The routed task overlay, exactly as the artist shell mounts it. `resetScroll: false` on both, for the
  // reason `DashboardRoute` gives: this opens a panel OVER where you already are, and scrolling to the top
  // is right for going somewhere and wrong for opening one.
  const openTask = (taskId: string) =>
    void navigate({ to: `/roster/artists/${artistId}`, search: { task: taskId }, resetScroll: false });
  const closeTask = () =>
    void navigate({ to: `/roster/artists/${artistId}`, search: {}, resetScroll: false });
  // The release card (ART-335/332): a release, at one of its sections.
  const openRelease = (releaseId: string, section?: string) =>
    void navigate({
      to: section
        ? `/roster/artists/${artistId}/releases/${releaseId}/${section}`
        : `/roster/artists/${artistId}/releases/${releaseId}`,
    });
  // Mounted OUTSIDE the subview switch: all three lenses land here, and a drawer that only one of them could
  // open would make the deep-link work or not depending on which org you happen to be looking through.
  const drawer = task ? <TaskDrawer taskId={task} onClose={closeTask} /> : null;
  // The phone dashboards (ART-358 / ART-361 / ART-360), one artist at a time; desktop below is unchanged.
  const wide = !usePhoneLayout();
  if (!wide && subview === "booking") return <><BookingMobileDashboard onOpenTask={openTask} />{drawer}</>;
  if (!wide && subview === "management")
    return (
      <>
        <ManagementMobileDashboard onOpenTask={openTask} onOpenRelease={(id) => openRelease(id)} onOpenTab={onOpenTab} />
        {drawer}
      </>
    );
  if (!wide && subview === "label")
    return <><LabelMobileDashboard onOpenTask={openTask} onOpenRelease={(id) => openRelease(id)} />{drawer}</>;
  switch (subview) {
    case "management":
      return <>
        <RosterArtistHeartbeat onOpenTab={onOpenTab} onOpenTask={openTask} onOpenRelease={openRelease} />
        {drawer}
      </>;
    case "booking":
      // "+ New offer" and "Send to team" are the dashboard's own (ART-326): a hold is born only when the
      // artist's team answers the request, never from the agent's board, so nothing here needs a route.
      // The To-do card's rows open the drawer here, as on the other two lenses (ART-324).
      return (
        <>
          <BookingArtistDashboard
            onOpenTask={openTask}
            // Clicking a hold/confirmed card opens that show OVER the dashboard, in place (ART-139) — the
            // editor for a hold, the read-only preview for a confirmed one — so closing returns to the
            // dashboard rather than the booking cockpit a navigation dropped onto. `onNavigate` is only the
            // preview's deep guest/door fallback, which has no in-place form.
            onNavigate={(route) => void navigate({ to: route })}
          />
          {drawer}
        </>
      );
    default:
      return <>
        <RosterArtistDashboard onOpenTab={onOpenTab} onOpenTask={openTask} onOpenRelease={openRelease} />
        {drawer}
      </>;
  }
}

/**
 * The roster booking cockpit — the SAME page the band gets at /booking, at the same URL.
 *
 * Which of the two Touring pages a lens lands on is decided by `rosterTabRoute`, not here: the shell maps
 * the `schedule` tab key to `booking` or `schedule` on the subview, mirroring `buildNavItems` on the artist
 * side. So the ROUTE picks the screen in both shells, and neither has a runtime branch that could disagree
 * with the label the nav is showing.
 *
 * Everything below is callback-driven, which is why this is a routing change and not a second page:
 * `BookingScreen`, `ShowEditorModal`, `ShowPreview` and `GuestListModal` take navigation as props and hold
 * no routes of their own.
 */
function RosterBookingRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { tab } = useSearch({ from: rosterBookingRoute.id });
  const base = `/roster/artists/${artistId}/booking`;

  return (
    <>
      <BookingScreen
        // Every overlay carries the CURRENT tab forward, and every one passes `resetScroll: false` — they
        // are modals over this still-mounted list, so none of them may throw it back to the top. Both rules
        // are the artist route's, kept verbatim rather than re-derived.
        onAddShow={() => void navigate({ to: `${base}/shows/new`, search: { step: 1, tab }, resetScroll: false })}
        onOpenShow={(showId) => void navigate({ to: `${base}/shows/${showId}`, search: { tab }, resetScroll: false })}
        onEditShow={(showId) =>
          void navigate({ to: `${base}/shows/${showId}/edit`, search: { step: 1, tab }, resetScroll: false })
        }
        tab={tab}
        onTabChange={(next) => void navigate({ to: base, search: { tab: next }, replace: true })}
      />
      <Outlet />
    </>
  );
}

/**
 * The roster show editor — one component for both URLs, so creating a show swaps the URL under an open dialog.
 *
 * It is also where a pre-hold OFFER becomes a hold. `?offer={id}` seeds the create form from that shortlist
 * row and, once the show exists, stamps the offer converted. Two rules make that hand-off honest:
 *
 * - The seed is resolved from the shortlist LIST already in cache (`useBookingOffers`), not from a fetch of
 *   its own. The board the agent just clicked is that list, so it is warm; a `?offer=` pointing at nothing —
 *   a stale link, a row somebody passed on in another tab — simply seeds nothing and the editor opens blank.
 *   That is the right failure: a create with no prefill still works.
 * - The convert fires on CREATED, not on saved. `onCreated` is the moment the show exists, and it is the only
 *   moment we hold both ids; `onSaved` can be reached again later by the same open dialog.
 */
function RosterShowEditorRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { showId } = useParams({ strict: false }) as { showId?: string };
  const { step, tab: rawTab, offer: offerId } = useSearch({ strict: false }) as {
    step?: number;
    tab?: unknown;
    offer?: string;
  };
  const activeStep = clampStep(step);
  const tab = bookingTab(rawTab);
  const base = `/roster/artists/${artistId}/booking`;
  const to = showId ? `${base}/shows/${showId}/edit` : `${base}/shows/new`;
  const refreshLists = useRefreshBookingLists();

  // Only a CREATE can be seeded, and only where the shortlist is readable at all — a seat without
  // `offers:manage` never has a board to convert from, so the read is gated rather than 403ing behind a
  // dialog that would still open correctly.
  const canOffers = useCapability("offers:manage");
  const seeding = !showId && !!offerId && canOffers;
  const offers = useBookingOffers({ enabled: seeding });
  const convert = useConvertBookingOffer();
  const sourceOffer = seeding ? offers.data?.offers?.find((o) => o.offerId === offerId) : undefined;
  const seed: ShowCreateSeed | undefined = sourceOffer && {
    date: sourceOffer.date,
    kind: sourceOffer.kind,
    venueName: sourceOffer.buyerVenue,
    guaranteeCents: sourceOffer.amountCents,
    // Only a TERMS offer carries a door split into the plan. Reading the percentage without its deal type
    // would tick the door split on a show whose offer was a flat guarantee.
    doorSplitPct: sourceOffer.dealType === "terms" ? sourceOffer.doorSplitPct : null,
  };

  return (
    <ShowEditorModal
      showId={showId}
      seed={seed}
      step={activeStep}
      onStep={(sn) => void navigate({ to, search: { step: sn, tab, offer: offerId }, resetScroll: false })}
      // Converting the pre-hold OFFER into this hold. It rides `onShowCreated`, NOT `onCreated`: a CLEAN create
      // dismisses the editor via `onSaved` and never calls `onCreated` (that fires only on the stay-open,
      // partial-failure path), so wiring the convert to `onCreated` left the offer `open` on every normal
      // conversion — a duplicate card on the board (ART-139). `onShowCreated` fires on any create. Guarded on
      // `sourceOffer` so a stale `?offer=` pointing at nothing (a passed/converted row) does not 409.
      // Awaited: the editor waits for the conversion to LAND before it dismisses and the lists refresh, or the
      // board refreshes with the offer still `open` and its card is still on it (ART-139).
      onShowCreated={async (id) => {
        if (offerId && sourceOffer) await convert.mutateAsync({ offerId, showId: id });
      }}
      // `replace`, so creating a show swaps the URL UNDER an open modal — Add and Edit stay one window. Only
      // fired when the editor STAYS open (a partial save); a clean create dismisses via `onSaved`.
      onCreated={(id) =>
        void navigate({ to: `${base}/shows/${id}/edit`, search: { step: 1, tab }, replace: true, resetScroll: false })
      }
      onExit={() => void navigate({ to: base, search: { tab }, resetScroll: false })}
      onDeleted={() => void navigate({ to: base, search: { tab }, resetScroll: false })}
      // A clean save refreshes the lists behind the scrim and lands on the tab the show now lives on — a
      // promoted hold has LEFT Holds, and returning to it reads as the confirm not having worked.
      onSaved={({ confirmed }) => {
        refreshLists();
        void navigate({ to: base, search: { tab: confirmed ? "confirmed" : tab }, resetScroll: false });
      }}
    />
  );
}

/** The roster show preview — a modal over the still-mounted list, like its artist twin. */
function RosterShowPreviewRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { showId } = useParams({ from: rosterShowPreviewRoute.id });
  const { tab } = useSearch({ from: rosterShowPreviewRoute.id });
  const refreshLists = useRefreshBookingLists();
  const base = `/roster/artists/${artistId}/booking`;
  return (
    <ShowPreview
      showId={showId}
      onEdit={(id) => void navigate({ to: `${base}/shows/${id}/edit`, search: { step: 1, tab }, resetScroll: false })}
      onOpenGuests={(id) => void navigate({ to: `${base}/shows/${id}/guests`, search: { tab }, resetScroll: false })}
      onBack={() => {
        refreshLists();
        void navigate({ to: base, search: { tab }, resetScroll: false });
      }}
    />
  );
}

/** The roster guest list — the same modal, closing back onto the roster booking list. */
function RosterGuestsModalRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { showId } = useParams({ from: rosterGuestsModalRoute.id });
  const { tab } = useSearch({ from: rosterGuestsModalRoute.id });
  return (
    <GuestListModal
      showId={showId}
      onClose={() => void navigate({ to: `/roster/artists/${artistId}/booking`, search: { tab } })}
    />
  );
}

/** The roster phone shows list — the band's `/shows` screen, its doors routed under this artist. */
function RosterShowsListRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  return (
    <MobileShowsScreen
      onOpenGuests={(showId) => void navigate({ to: `/roster/artists/${artistId}/shows/${showId}/guests` })}
      onOpenLogistics={(showId) => void navigate({ to: `/roster/artists/${artistId}/shows/${showId}/logistics` })}
    />
  );
}

/** The roster door check-in — back returns to where it was opened from, else the artist's shows list. */
function RosterDoorRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { artistId, showId } = useParams({ from: rosterDoorRoute.id });
  return (
    <RequireBand>
      <DoorCheckInScreen
        showId={showId}
        onBack={() =>
          router.history.canGoBack() ? router.history.back() : void navigate({ to: `/roster/artists/${artistId}/shows` })
        }
      />
    </RequireBand>
  );
}

/** The roster show logistics — the band's screen, backing out to the artist's shows list on a cold open. */
function RosterShowLogisticsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { artistId, showId } = useParams({ from: rosterShowLogisticsRoute.id });
  return (
    <ShowLogisticsScreen
      showId={showId}
      onBack={() =>
        router.history.canGoBack() ? router.history.back() : void navigate({ to: `/roster/artists/${artistId}/shows` })
      }
    />
  );
}

/** The roster Releases hub — the same ReleasesScreen under the org lens, with roster-prefixed navigation. */
function RosterReleasesRoute() {
  const navigate = useNavigate();
  const { artistId } = useParams({ from: rosterArtistRoute.id });
  const { release } = useSearch({ from: rosterReleasesRoute.id });
  // The artist route's swap: a phone gets the read-only mobile releases screen, never the desktop hub.
  const isDesktop = !usePhoneLayout();
  if (!isDesktop) return <MobileReleasesScreen initialReleaseId={release} />;
  return (
    <ReleasesScreen
      onOpenRelease={(releaseId, section) =>
        void navigate({ to: `/roster/artists/${artistId}/releases/${releaseId}/${section ?? DEFAULT_SECTION}` })
      }
      // The routed new-release modal is not mirrored under the roster lens at the shell stage; keep the CTA
      // benign (stays on the hub). Creating a release from the roster lens lands with the Label/Mgmt subviews.
      onNewRelease={() => void navigate({ to: `/roster/artists/${artistId}/releases` })}
    />
  );
}

/** The roster release command center — the section rides the URL; renders the matching section component. */
function RosterReleaseDetailRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const { artistId, releaseId, section } = useParams({ from: rosterReleaseSectionRoute.id });
  const active = RELEASE_SECTIONS.some((s) => s.key === section) ? (section as ReleaseSectionKey) : DEFAULT_SECTION;
  // A phone has no single-release page (the artist route's rule): the releases screen, opened on this release.
  const phone = usePhoneLayout();
  if (phone) {
    return <Navigate to="/roster/artists/$artistId/releases" params={{ artistId }} search={{ release: releaseId }} replace />;
  }
  return (
    <ReleaseDetailScreen
      releaseId={releaseId}
      activeSection={active}
      onSection={(key) =>
        void navigate({ to: `/roster/artists/${artistId}/releases/${releaseId}/${key}`, resetScroll: false, replace: true })
      }
      onBack={() =>
        router.history.canGoBack()
          ? router.history.back()
          : void navigate({ to: `/roster/artists/${artistId}/releases` })
      }
    >
      {renderReleaseSection(active, releaseId)}
    </ReleaseDetailScreen>
  );
}

/** Render a release section by key (the roster mirror of the artist side's 8 section routes). */
function renderReleaseSection(key: ReleaseSectionKey, releaseId: string) {
  switch (key) {
    case "assets":
      return <AssetsSection releaseId={releaseId} />;
    case "todo":
      return <ToDoSection releaseId={releaseId} />;
    case "smart-link":
      return <SmartLinkSection releaseId={releaseId} />;
    case "ads":
      return <LiveAdsSection releaseId={releaseId} />;
    case "details":
      return <DetailsSection releaseId={releaseId} />;
    case "timeline":
      return <TimelineSection releaseId={releaseId} />;
    case "budget":
      return <BudgetSection releaseId={releaseId} />;
    default:
      return <OverviewSection releaseId={releaseId} />;
  }
}

/** The roster artist Settings — the same SettingsScreen under the org lens, tab-driven by the URL. */
function RosterArtistSettingsRoute() {
  const navigate = useNavigate();
  const { artistId, tab } = useParams({ from: rosterSettingsTabRoute.id });
  const activeTab: SettingsTab =
    // `features` is hidden for now (settingsTabs.ts) and falls through to Account.
    tab === "connections" ? "connections" : tab === "profile" ? "profile" : "account";
  return (
    <SettingsScreen activeTab={activeTab} onTab={(t) => void navigate({ to: `/roster/artists/${artistId}/settings/${t}` })}>
      {activeTab === "connections" ? (
        <SettingsConnectionsTab />
      ) : activeTab === "profile" ? (
        <SettingsProfileTab />
      ) : (
        <SettingsAccountTab />
      )}
    </SettingsScreen>
  );
}

/** The admin console's layout — no band, org or realtime; just the console's own frame. */
function AdminShellLayout() {
  const navigate = useNavigate();
  const auth = useAuth();
  async function onLogout() {
    await auth.logout();
    await navigate({ to: "/login" });
  }
  return (
    <>
      <AdminShell onLogout={() => void onLogout()}>
        <Outlet />
      </AdminShell>
      <ToastHost />
    </>
  );
}

function AdminRoute() {
  const navigate = useNavigate();
  const auth = useAuth();
  // Signing in again is how a production admin on a password-only session gets a second-factor one; /login
  // brings them straight back here afterwards.
  async function signInAgain() {
    await auth.logout();
    await navigate({ to: "/login", search: { redirect: "/admin" } });
  }
  const { tab, band, id } = adminRoute.useSearch();
  return (
    <AdminConsole
      activeTab={parseAdminTab(tab)}
      onTab={(next) => void navigate({ to: "/admin", search: { tab: next }, replace: true })}
      bandId={band}
      // Choosing a band keeps you on Integrations (a band picked from "Needs attention" opens its jobs below).
      onBand={(next) => void navigate({ to: "/admin", search: { tab: "integrations", band: next }, replace: true })}
      applicationId={id}
      onApplication={(next) => void navigate({ to: "/admin", search: { tab: "applications", id: next }, replace: true })}
      onSignInAgain={() => void signInAgain()}
    />
  );
}

/** The partner home (ART-239). */
function PartnerRoute() {
  const navigate = useNavigate();
  return (
    <PartnerScreen
      onOpenShow={(showId) => void navigate({ to: "/dos", search: { show: showId } })}
      onOpenEvent={(eventId) => void navigate({ to: "/partner/events/$eventId", params: { eventId } })}
    />
  );
}

/** Back from a partner detail page: a history-back when there is one (so the iOS edge swipe and the Back button
 *  land in the same place — the ART-268 rule), else the partner home for a cold open. Registered with the mobile
 *  back store too, which is what offers the native swipe on this page. */
function usePartnerBack() {
  const router = useRouter();
  const navigate = useNavigate();
  const onBack = () => (router.history.canGoBack() ? router.history.back() : void navigate({ to: "/partner" }));
  useMobileBack(onBack);
  return onBack;
}

/** A partner's Day of Show — their own screen at the band's URL (`/dos?show=`), rendered inside the partner frame by
 *  ShellLayout. No show named ⇒ their home, which is where every one of their shows is listed. */
function PartnerDosRoute({ showId }: { showId: string | undefined }) {
  const onBack = usePartnerBack();
  if (!showId) return <Navigate to="/partner" replace />;
  return <PartnerDetailScreen kind="show" id={showId} onBack={onBack} />;
}

function PartnerEventRoute() {
  const { eventId } = useParams({ from: partnerEventRoute.id });
  const onBack = usePartnerBack();
  return <PartnerDetailScreen kind="event" id={eventId} onBack={onBack} />;
}

function PartnerShellLayout() {
  return <PartnerFrame />;
}

/**
 * The partner's frame (ART-239) — PartnerShell around the routed page, with the realtime hub so their screens refresh
 * live: their own `user:` group (a "you're on the list" notification) and, on a Day of Show, that show's group. Used
 * by their `/partner` routes AND by ShellLayout when they open `/dos`, so the band's URL renders in their frame.
 */
function PartnerFrame() {
  const navigate = useNavigate();
  const auth = useAuth();
  async function onLogout() {
    await auth.logout();
    await navigate({ to: "/login" });
  }
  return (
    <RealtimeProvider>
      <PartnerShell onLogout={() => void onLogout()}>
        <Outlet />
      </PartnerShell>
      <ToastHost />
    </RealtimeProvider>
  );
}

/**
 * Turns an `openMessageWith` request into a navigation where the inbox is a PAGE rather than the topbar overlay
 * (ART-264). `InboxButton` consumes the same store on desktop; the two are exclusive by the same breakpoint the
 * shell swaps on, so exactly one of them takes each request.
 */
function MessageRequestBridge({ active, onRequest }: { active: boolean; onRequest: (personId: string) => void }) {
  const pending = useMessagePanel((s) => s.personId);
  const consume = useMessagePanel((s) => s.consume);
  useEffect(() => {
    if (!active || !pending) return;
    consume();
    onRequest(pending);
  }, [active, pending, consume, onRequest]);
  return null;
}

function ShellLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  // Shell selection: the Capacitor platform OR a viewport below `md` mounts the MobileShell; the
  // wider desktop artist root mounts ArtistShell. Both wrap the SAME routed <Outlet/> — one route tree, two
  // chromes — so mobile-only routes (/dos, /shows/:id/{guests,vip,logistics}) and the shared
  // surfaces render identically under either.
  //
  // AUTO-SWAPS on a runtime viewport crossing the shell breakpoint (ART-167) — a web resize or a tablet
  // rotation flips the chrome rather than the choice being latched at mount. `useMinWidth` returns a BOOLEAN
  // that only changes VALUE at the crossing, so the shell swaps exactly ONCE per crossing (never per pixel) —
  // there is no per-pixel thrash to debounce, and a trailing debounce only made the swap feel ~100px late
  // (during a continuous drag the resize events never stop, so the debounced value never settled until you
  // paused). Swapping the component type here does unmount + remount the routed <Outlet/> subtree (in-progress
  // module state is discarded) — an accepted cost of the crossing, which a debounce would delay, not prevent.
  // Realtime is unaffected — the RealtimeProvider below wraps BOTH branches (keyed on the acting person, not
  // the shell), so a swap changes the chrome without touching the hub connection. isNative is constant per
  // session and always resolves to mobile; only the web viewport half is reactive.
  //
  // The threshold is `lg` (900px): below it — tablet-portrait, a split-screen half, a narrow window — the
  // bottom-tab MobileShell; at/above it the desktop ArtistShell. `md` (720) was too tight (you had to make the
  // window quite small before the mobile chrome appeared); `xl` would steal desktop from ordinary laptops.
  // The ONE phone/desktop decision (`app/phoneLayout.ts`), published below so every screen reads it.
  const mobile = useShellPhoneDecision();
  const isPartner = useMe().data?.isPartnerViewer === true;
  // Null on every ordinary session; set only by the DEV fixture switcher. See the RealtimeProvider key below.
  const actingAs = useActingAs();
  // Register this device for the signed-in person's push notifications — here rather than in either shell, so a
  // breakpoint swap between the two never re-runs it (ART-259).
  usePushRegistration();
  // Someone has signed in on this phone, so its signed-out screen is Log in from now on, not Welcome (ART-346).
  useEffect(() => void markSignedInOnThisDevice(), []);
  // The DOS lock-screen card, automatic across every band (ART-208) — mounted beside push for the same reason.
  useLiveActivitySync();

  // Theme (ART-174). The dark palette is set on <html> so portalled overlays (Modal/Sheet → document.body) and
  // the global body background inherit it. The preference is a DEV-ONLY control (forced light in production);
  // DOS forces dark at every width regardless — resolveEffectiveTheme owns that rule. The write effect overwrites on
  // change; a separate mount-only effect removes the attribute when the authed shell unmounts (e.g. → /login),
  // so a stray "dark" never survives logout.
  const themePreference = useThemePreference((s) => s.preference);
  const effectiveTheme = resolveEffectiveTheme({
    pathname: location.pathname,
    mobile,
    preference: IS_NON_PRODUCTION ? themePreference : "light",
  });
  useEffect(() => {
    document.documentElement.dataset.theme = effectiveTheme;
  }, [effectiveTheme]);
  useEffect(
    () => () => {
      delete document.documentElement.dataset.theme;
    },
    [],
  );
  async function onLogout() {
    await auth.logout();
    await navigate({ to: "/login" });
  }
  const onNavigate = (route: string) => void navigate({ to: route });
  // A seatless PARTNER reaches this layout only on `/dos` (the shell guard sends them anywhere else to /partner):
  // their Day of Show takes the band's URL but renders in THEIR frame, never the band's chrome (ART-239).
  if (isPartner) return <PartnerFrame />;
  // RealtimeProvider mounts INSIDE the authed shell route (its beforeLoad guard has already run), so the one
  // hub connection lives exactly the authenticated session — it starts here (a bearer exists to mint the WS
  // ticket) and stops when this layout unmounts on logout → /login. ToastHost renders the transient
  // mutation-error stack app-wide within the authed session.
  return (
    // KEYED on the acting person. The hub connection is PERSON-bound (its WS ticket authenticates the person)
    // and RealtimeProvider starts it once on mount, so a DEV seat swap would otherwise leave the socket
    // authenticated as the OWNER — the member's view receiving the owner's `user:{id}` events, in the exact
    // surface someone swapped seats to test. Changing the key unmounts and remounts, which IS stop-then-start
    // under the provider's existing lifetime contract, rather than a restart method invented for one dev
    // affordance. Constant ("self") in production and on every ordinary session, so nothing remounts there.
    <RealtimeProvider key={actingAs?.personId ?? "self"}>
      <PhoneLayoutProvider phone={mobile}>
      {mobile ? (
        <MobileShell onLogout={() => void onLogout()} onNavigate={onNavigate} activeRoute={location.pathname} theme={effectiveTheme}>
          <Outlet />
        </MobileShell>
      ) : (
        <ArtistShell onLogout={() => void onLogout()} onNavigate={onNavigate} activeRoute={location.pathname}>
          <Outlet />
        </ArtistShell>
      )}
      <ProfileHost pathname={location.pathname} onOpenBand={(bandId) => void navigate({ to: "/contacts/band/$bandId", params: { bandId } })} />
      {/* On a phone there is no inbox OVERLAY (InboxButton is desktop chrome), so "message this person" becomes
          a navigation to the inbox page carrying who — or the profile's Message button does nothing (ART-264). */}
      <MessageRequestBridge active={mobile} onRequest={(personId) => void navigate({ to: "/messages", search: { with: personId } })} />
      <ToastHost />
      </PhoneLayoutProvider>
    </RealtimeProvider>
  );
}
